import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Briefcase, Plus, FileText, Eye, Save, Download, Edit2, Trash2,
  MessageSquare, Clock, CheckCircle, XCircle, Send, ChevronDown, ChevronUp, Link2,
  FileSignature, DollarSign, Calculator, Search, AlertTriangle, ListTree, GitBranch, Layers,
  Lightbulb, ArrowRight, Calendar, User, Users, Building, Tag, FilePlus, Play,
  Upload, FileCheck2
} from 'lucide-react';
import api, { formatErrorMessage, settleRequests } from '../utils/api';
import { toast } from 'sonner';
import AnexosContractuales from '../components/AnexosContractuales';
import { CuotasCalculator } from '../components/CuotasCalculator';

// Constante de conversión UF a CLP
const UF_TO_CLP = 40000;

const STATUS_OPTIONS = [
  { value: 'Borrador', label: 'Borrador', color: 'bg-slate-100 text-slate-700' },
  { value: 'En Negociación', label: 'En Negociación', color: 'bg-amber-100 text-amber-700' },
  { value: 'Enviada', label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  { value: 'Primer Pago', label: 'Primer Pago', color: 'bg-orange-100 text-orange-700' },
  { value: 'Aceptada', label: 'Aceptada', color: 'bg-green-100 text-green-700' },
  { value: 'Ejecución/Activa', label: 'Ejecución/Activa', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'Rechazada', label: 'Rechazada', color: 'bg-red-100 text-red-700' },
  { value: 'Cerrada por Fragmentación', label: 'Cerrada por Fragmentación', color: 'bg-purple-100 text-purple-700' },
];

const Propuestas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [showNewProposal, setShowNewProposal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [expandedBitacora, setExpandedBitacora] = useState({});
  const [newBitacoraEntry, setNewBitacoraEntry] = useState({ tipo: '', descripcion: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('borradores');
  const [showCPSViewer, setShowCPSViewer] = useState(false);
  const [selectedCPS, setSelectedCPS] = useState(null);
  
  // Estado para eliminar PSP
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Estados para minutas N:N
  const [availableMinutas, setAvailableMinutas] = useState([]);
  const [selectedMinutas, setSelectedMinutas] = useState([]);
  const [proposalMinutas, setProposalMinutas] = useState([]);
  const [showMinutasModal, setShowMinutasModal] = useState(false);
  
  // Estado para búsqueda
  const [searchQuery, setSearchQuery] = useState('');

  // Estado para tab Aceptadas (búsqueda local + colapsado)
  const [aceptadasSearch, setAceptadasSearch] = useState('');
  const [expandedAceptadasClientes, setExpandedAceptadasClientes] = useState({});
  const [expandedAceptadasPSPs, setExpandedAceptadasPSPs] = useState({});
  
  // Estado para ítems de cobro (jerarquía de cobro)
  const [itemsCobro, setItemsCobro] = useState([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    tipo: 'tarea',
    nombre: '',
    descripcion: '',
    valor_uf: '',
    tarea_ids: []
  });
  
  // Estado para eliminar ítem de cobro
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  
  // Estado para tareas disponibles de las minutas
  const [tareasDisponibles, setTareasDisponibles] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  
  // Estado para modal de datos de facturación + contrato
  const [showFacturacionModal, setShowFacturacionModal] = useState(false);
  const [proposalToAccept, setProposalToAccept] = useState(null);
  const [datosFacturacion, setDatosFacturacion] = useState({
    nombre_razon_social: '',
    rut: '',
    contacto_nombre: '',
    contacto_email: '',
    contacto_telefono: '',
    notas: ''
  });
  const [datosContrato, setDatosContrato] = useState({
    representante_legal: '',
    rut_representante: '',
    domicilio_legal: ''
  });
  const [usarMismosDatos, setUsarMismosDatos] = useState(false);
  
  // Estado para ver datos de facturación existentes
  const [showViewFacturacionModal, setShowViewFacturacionModal] = useState(false);
  const [viewingFacturacion, setViewingFacturacion] = useState(null);
  
  // Estados para fragmentación de PSP
  const [showFragmentacionModal, setShowFragmentacionModal] = useState(false);
  const [fragmentacionPSP, setFragmentacionPSP] = useState(null);
  const [tareasFragmentables, setTareasFragmentables] = useState([]);
  const [loadingFragmentacion, setLoadingFragmentacion] = useState(false);
  const [tareasSeleccionadasFrag, setTareasSeleccionadasFrag] = useState([]);
  const [tareasDiferirFrag, setTareasDiferirFrag] = useState([]); // Tareas a diferir como oportunidad
  const [fragmentacionForm, setFragmentacionForm] = useState({
    categoria_servicio: '',
    descripcion_alcance: '',
    precio_uf: '',
    tipo_servicio: 'Spot'
  });
  const [pspsRelacionadas, setPspsRelacionadas] = useState({ psp_madre: null, psps_hijas: [] });
  
  // Estados para oportunidades diferidas
  const [oportunidadesDiferidas, setOportunidadesDiferidas] = useState([]);
  const [loadingOportunidades, setLoadingOportunidades] = useState(false);
  const [selectedOportunidades, setSelectedOportunidades] = useState([]);
  const [showConvertirModal, setShowConvertirModal] = useState(false);
  const [convertirForm, setConvertirForm] = useState({
    titulo: '',
    descripcion: '',
    precio_uf: '',
    tipo_servicio: 'Spot',
    meses_ejecucion: 3,
    num_cuotas: 1,
    // Inicio diferido de cobranza
    billing_start_mode: 'immediate',
    billing_start_date: '',
    related_psp_id: ''
  });
  
  // Estados para Anexos Contractuales
  const [showAnexosModal, setShowAnexosModal] = useState(false);
  const [selectedPSPAnexo, setSelectedPSPAnexo] = useState(null);
  
  // Tipos de servicio comercial
  const TIPOS_SERVICIO = [
    { value: 'Spot', label: 'Spot (Puntual)' },
    { value: 'Mensual', label: 'Mensual' },
    { value: 'Proyecto Único', label: 'Proyecto Único' },
    { value: 'Recurrente', label: 'Recurrente' },
    { value: 'Otro', label: 'Otro' }
  ];
  
  const [newProposal, setNewProposal] = useState({
    client_id: '',
    titulo: '',
    descripcion: '',
    precio_uf: '',
    meses_ejecucion: '',
    num_cuotas: '',
    tipo_servicio: 'Spot',
    categoria_servicio: '',
    // Estructura de pago avanzada
    usar_pago_avanzado: false,
    reparto_mode: 'iguales',
    primera_cuota_uf: '',
    honorario_resultado_texto: '',
    descuento_pago_anticipado: false,
    fecha_primera_cuota: '',
    cuotas_personalizadas: [],
    // Inicio diferido de cobranza
    billing_start_mode: 'immediate',
    billing_start_date: '',
    related_psp_id: '',
    // Áreas responsables (para PSPs sin minuta)
    areas_responsables: [],
  });
  
  // Estado para creación inline de prospecto desde "Nueva PSP"
  const [showNewProspect, setShowNewProspect] = useState(false);
  const [newProspect, setNewProspect] = useState({
    name: '',
    company: '',
    rut: '',
    email: '',
    phone: '',
    origen: ''
  });
  const [savingProspect, setSavingProspect] = useState(false);
  
  // Catálogos
  const ORIGEN_OPTIONS = [
    { value: 'Meta (Facebook/Instagram)', label: 'Meta (Facebook/Instagram)' },
    { value: 'Google Ads', label: 'Google Ads' },
    { value: 'Referido', label: 'Referido' },
    { value: 'Nuevo Servicio', label: 'Nuevo Servicio' },
    { value: 'Nuevo Cliente', label: 'Nuevo Cliente' }
  ];
  const AREAS_OPTIONS = [
    { value: 'Tributaria', label: 'Tributaria' },
    { value: 'Legal', label: 'Legal' },
    { value: 'Contabilidad', label: 'Contabilidad' },
    { value: 'Comercial', label: 'Comercial' },
    { value: 'Adm. y Finanzas', label: 'Adm. y Finanzas' }
  ];
  
  const [editForm, setEditForm] = useState({
    titulo: '',
    descripcion: '',
    precio_uf: '',
    meses_ejecucion: '',
    num_cuotas: '',
    // Estructura de pago avanzada
    usar_pago_avanzado: false,
    reparto_mode: 'iguales',
    primera_cuota_uf: '',
    honorario_resultado_texto: '',
    descuento_pago_anticipado: false,
    fecha_primera_cuota: '',
    cuotas_personalizadas: [],
    // Inicio diferido de cobranza
    billing_start_mode: 'immediate',
    billing_start_date: '',
    related_psp_id: '',
  });
  
  // PSPs disponibles para subordinación (inicio diferido)
  const [availablePspsForSubordination, setAvailablePspsForSubordination] = useState([]);

  // Función para convertir UF a CLP
  const ufToCLP = (uf) => Math.round(uf * UF_TO_CLP);
  
  // Función para formatear UF
  const formatUF = (value) => {
    if (!value && value !== 0) return '-';
    return `${Number(value).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} UF`;
  };
  
  // Función para formatear CLP
  const formatCLP = (value) => {
    if (!value && value !== 0) return '-';
    return `$${Number(value).toLocaleString('es-CL')}`;
  };

  // Función para filtrar propuestas por búsqueda
  const filterProposals = (proposalsList) => {
    if (!searchQuery.trim()) return proposalsList;
    
    const query = searchQuery.toLowerCase().trim();
    
    return proposalsList.filter(proposal => {
      const client = clients.find(c => c.id === proposal.client_id);
      
      // Buscar por título de propuesta
      const titulo = (proposal.titulo || '').toLowerCase();
      // Buscar por folio
      const folio = (proposal.folio || '').toLowerCase();
      
      // Si coincide con título o folio, incluir sin importar el cliente
      if (titulo.includes(query) || folio.includes(query)) {
        return true;
      }

      // Fallback a prospecto_snapshot cuando la PSP no tiene cliente resuelto
      // (PSPs creadas desde un prospecto: client_id=null, prospecto_id=<id>).
      if (!client) {
        const snapNombre = (proposal.prospecto_snapshot?.nombre || '').toLowerCase();
        const snapEmpresa = (proposal.prospecto_snapshot?.empresa || '').toLowerCase();
        return snapNombre.includes(query) || snapEmpresa.includes(query);
      }
      
      // Buscar por nombre del prospecto/cliente
      const nombre = (client.prospecto || client.name || '').toLowerCase();
      // Buscar por empresa
      const empresa = (client.empresa || client.company || '').toLowerCase();
      // Buscar por RUT
      const rut = (client.rut || '').toLowerCase();
      // Buscar por correo
      const email = (client.email || '').toLowerCase();
      
      return nombre.includes(query) || 
             empresa.includes(query) || 
             rut.includes(query) || 
             email.includes(query);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Verificar si el usuario es administrador
  const isAdmin = currentUser?.role === 'Administrador';

  // Función para eliminar PSP
  const handleDeleteProposal = async () => {
    if (!proposalToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/proposals/${proposalToDelete.id}`);
      toast.success(`PSP ${proposalToDelete.folio} eliminada correctamente`);
      setShowDeleteDialog(false);
      setProposalToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const fetchData = async () => {
    try {
      const { values, fallidos } = await settleRequests({
        'clients':                        api.get('/clients'),
        'proposals':                      api.get('/proposals'),
        'meetings/disponibles-para-psp':  api.get('/meetings/disponibles-para-psp'),
      });
      if (values['clients'])                       setClients(values['clients'].data);
      if (values['proposals'])                     setProposals(values['proposals'].data);
      if (values['meetings/disponibles-para-psp']) setAvailableMinutas(values['meetings/disponibles-para-psp'].data);
      if (fallidos.length) {
        console.error('[Propuestas] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Ventas · ${f.detalle}`, { duration: 8000 }));
      }

      // Obtener usuario actual (manejando error si no existe)
      try {
        const userRes = await api.get('/users/me');
        setCurrentUser(userRes.data);
      } catch (userError) {
        console.warn('No se pudo obtener info del usuario');
      }
    } catch (error) {
      console.error('[Propuestas] Error inesperado:', error);
      toast.error('Error inesperado en Propuestas/Ventas');
    }
  };

  // Cargar PSPs disponibles para subordinación cuando cambia el cliente
  const fetchAvailablePspsForSubordination = async (clientId) => {
    if (!clientId) {
      setAvailablePspsForSubordination([]);
      return;
    }
    try {
      const res = await api.get(`/proposals/${clientId}/available-for-subordination`);
      setAvailablePspsForSubordination(res.data);
    } catch (error) {
      console.warn('Error cargando PSPs para subordinación:', error);
      setAvailablePspsForSubordination([]);
    }
  };

  const handleCreateProspectInline = async () => {
    if (!newProspect.name.trim()) {
      toast.error('El nombre es obligatorio');
      return null;
    }
    if (!newProspect.origen) {
      toast.error('Selecciona el origen del prospecto');
      return null;
    }
    setSavingProspect(true);
    try {
      const payload = {
        name: newProspect.name.trim(),
        company: newProspect.company.trim() || null,
        rut: newProspect.rut.trim() || null,
        email: newProspect.email.trim() || null,
        phone: newProspect.phone.trim() || null,
        origen: newProspect.origen,
        is_prospect: true
      };
      const res = await api.post('/clients', payload);
      toast.success('Prospecto creado');
      // Refrescar lista de clientes
      await fetchData();
      setNewProspect({ name: '', company: '', rut: '', email: '', phone: '', origen: '' });
      setShowNewProspect(false);
      return res.data?.id || res.data?.client?.id;
    } catch (e) {
      toast.error(formatErrorMessage(e));
      return null;
    } finally {
      setSavingProspect(false);
    }
  };
  
  const handleCreateProposal = async () => {
    try {
      // Si está abierto el form de nuevo prospecto, crear primero
      let clientId = newProposal.client_id;
      if (showNewProspect) {
        const newId = await handleCreateProspectInline();
        if (!newId) return;
        clientId = newId;
      }
      if (!clientId) {
        toast.error('Selecciona un cliente o crea un nuevo prospecto');
        return;
      }
      
      const precioUF = parseFloat(newProposal.precio_uf);
      
      const payload = {
        client_id: clientId,
        titulo: newProposal.titulo,
        descripcion: newProposal.descripcion,
        precio_uf: precioUF,
        precio_total: ufToCLP(precioUF),
        meses_ejecucion: parseInt(newProposal.meses_ejecucion),
        num_cuotas: parseInt(newProposal.num_cuotas),
        meeting_ids: selectedMinutas,
        tipo_servicio: newProposal.tipo_servicio,
        categoria_servicio: newProposal.categoria_servicio,
        // Estructura de pago
        reparto_mode: newProposal.reparto_mode || 'iguales',
        primera_cuota_uf: newProposal.reparto_mode === 'inicial_saldo' && newProposal.primera_cuota_uf 
          ? parseFloat(newProposal.primera_cuota_uf) : null,
        honorario_resultado_texto: newProposal.honorario_resultado_texto || null,
        descuento_pago_anticipado: newProposal.descuento_pago_anticipado || false,
        fecha_primera_cuota: newProposal.fecha_primera_cuota || null,
        // Cuotas personalizadas (si el usuario editó la tabla)
        cuotas_personalizadas: newProposal.cuotas_personalizadas?.length > 0 
          ? newProposal.cuotas_personalizadas : null,
        // Inicio diferido de cobranza
        billing_start_mode: newProposal.billing_start_mode || 'immediate',
        billing_start_date: newProposal.billing_start_mode === 'fixed_date' && newProposal.billing_start_date 
          ? new Date(newProposal.billing_start_date).toISOString() : null,
        related_psp_id: newProposal.billing_start_mode === 'after_related_psp' 
          ? newProposal.related_psp_id : null,
        // Áreas responsables (PSP sin minuta)
        areas_responsables: newProposal.areas_responsables || [],
      };

      await api.post('/proposals', payload);
      toast.success('PSP creada exitosamente');
      setShowNewProposal(false);
      setNewProposal({
        client_id: '',
        titulo: '',
        descripcion: '',
        precio_uf: '',
        meses_ejecucion: '',
        num_cuotas: '',
        tipo_servicio: 'Spot',
        categoria_servicio: '',
        usar_pago_avanzado: false,
        reparto_mode: 'iguales',
        primera_cuota_uf: '',
        honorario_resultado_texto: '',
        descuento_pago_anticipado: false,
        fecha_primera_cuota: '',
        cuotas_personalizadas: [],
        billing_start_mode: 'immediate',
        billing_start_date: '',
        related_psp_id: '',
        areas_responsables: [],
      });
      setSelectedMinutas([]);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // ============ FUNCIONES DE FRAGMENTACIÓN DE PSP ============
  
  const openFragmentacionModal = async (proposal) => {
    setFragmentacionPSP(proposal);
    setLoadingFragmentacion(true);
    setTareasSeleccionadasFrag([]);
    setTareasDiferirFrag([]);
    setFragmentacionForm({
      categoria_servicio: '',
      descripcion_alcance: '',
      precio_uf: '',
      tipo_servicio: 'Spot'
    });
    setShowFragmentacionModal(true);
    
    try {
      // Cargar tareas fragmentables
      const response = await api.get(`/proposals/${proposal.id}/tareas-fragmentables`);
      // El backend devuelve directamente un array de tareas
      setTareasFragmentables(Array.isArray(response.data) ? response.data : []);
      
      // Cargar PSPs relacionadas (madre/hijas)
      const relResponse = await api.get(`/proposals/${proposal.id}/fragmentaciones`);
      setPspsRelacionadas(relResponse.data);
    } catch (error) {
      toast.error('Error al cargar datos de fragmentación');
      console.error(error);
    } finally {
      setLoadingFragmentacion(false);
    }
  };
  
  // Función para manejar selección de tarea para diferir
  const handleToggleTareaDiferir = (tareaId) => {
    // Si ya está en PSP, quitarla de ahí primero
    if (tareasSeleccionadasFrag.includes(tareaId)) {
      setTareasSeleccionadasFrag(prev => prev.filter(id => id !== tareaId));
    }
    
    setTareasDiferirFrag(prev => {
      if (prev.includes(tareaId)) {
        return prev.filter(id => id !== tareaId);
      } else {
        return [...prev, tareaId];
      }
    });
  };
  
  // Función para manejar selección de tarea para PSP
  const handleToggleTareaPSP = (tareaId) => {
    // Si ya está para diferir, quitarla de ahí primero
    if (tareasDiferirFrag.includes(tareaId)) {
      setTareasDiferirFrag(prev => prev.filter(id => id !== tareaId));
    }
    
    setTareasSeleccionadasFrag(prev => {
      if (prev.includes(tareaId)) {
        return prev.filter(id => id !== tareaId);
      } else {
        return [...prev, tareaId];
      }
    });
  };
  
  const handleCrearFragmentacion = async () => {
    if (tareasSeleccionadasFrag.length === 0 && tareasDiferirFrag.length === 0) {
      toast.error('Debe seleccionar al menos una tarea para fragmentar o diferir');
      return;
    }
    if (tareasSeleccionadasFrag.length > 0 && !fragmentacionForm.categoria_servicio) {
      toast.error('Debe seleccionar una categoría de servicio para la nueva PSP');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        task_ids: tareasSeleccionadasFrag,
        task_ids_diferir: tareasDiferirFrag,
        categoria_servicio: fragmentacionForm.categoria_servicio,
        descripcion_alcance: fragmentacionForm.descripcion_alcance || null,
        precio_uf: fragmentacionForm.precio_uf ? parseFloat(fragmentacionForm.precio_uf) : null,
        tipo_servicio: fragmentacionForm.tipo_servicio || 'Spot'
      };
      
      const response = await api.post(`/proposals/${fragmentacionPSP.id}/fragmentar`, payload);
      
      // Mostrar mensajes según resultado
      if (response.data.nueva_psp_folio) {
        toast.success(`PSP fragmentada creada: ${response.data.nueva_psp_folio}`);
      }
      if (response.data.tareas_diferidas > 0) {
        toast.info(`${response.data.tareas_diferidas} tarea(s) guardada(s) como oportunidad futura`);
      }
      
      setShowFragmentacionModal(false);
      fetchData();
      // Recargar oportunidades si estamos en esa pestaña
      if (activeTab === 'oportunidades') {
        fetchOportunidadesDiferidas();
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  
  // ============ FUNCIONES DE OPORTUNIDADES DIFERIDAS ============
  
  const fetchOportunidadesDiferidas = async () => {
    setLoadingOportunidades(true);
    try {
      const response = await api.get('/oportunidades-diferidas');
      setOportunidadesDiferidas(response.data || []);
    } catch (error) {
      console.error('Error fetching oportunidades:', error);
    } finally {
      setLoadingOportunidades(false);
    }
  };
  
  const handleSelectOportunidad = (opId) => {
    setSelectedOportunidades(prev => {
      if (prev.includes(opId)) {
        return prev.filter(id => id !== opId);
      } else {
        return [...prev, opId];
      }
    });
  };
  
  const handleConvertirOportunidades = async () => {
    if (selectedOportunidades.length === 0) {
      toast.error('Debe seleccionar al menos una oportunidad');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        oportunidad_ids: selectedOportunidades,
        psp_data: {
          titulo: convertirForm.titulo || 'Nueva propuesta desde oportunidades',
          descripcion: convertirForm.descripcion,
          precio_uf: parseFloat(convertirForm.precio_uf) || 10,
          tipo_servicio: convertirForm.tipo_servicio,
          meses_ejecucion: parseInt(convertirForm.meses_ejecucion) || 3,
          num_cuotas: parseInt(convertirForm.num_cuotas) || 1,
          // Inicio diferido de cobranza
          billing_start_mode: convertirForm.billing_start_mode || 'immediate',
          billing_start_date: convertirForm.billing_start_mode === 'fixed_date' && convertirForm.billing_start_date 
            ? new Date(convertirForm.billing_start_date).toISOString() : null,
          related_psp_id: convertirForm.billing_start_mode === 'after_related_psp' 
            ? convertirForm.related_psp_id : null,
        }
      };
      
      const response = await api.post('/oportunidades-diferidas/convertir-multiples', payload);
      toast.success(`PSP ${response.data.psp_folio} creada con ${response.data.tareas_creadas} tareas`);
      
      setShowConvertirModal(false);
      setSelectedOportunidades([]);
      setConvertirForm({
        titulo: '',
        descripcion: '',
        precio_uf: '',
        tipo_servicio: 'Spot',
        meses_ejecucion: 3,
        num_cuotas: 1,
        billing_start_mode: 'immediate',
        billing_start_date: '',
        related_psp_id: ''
      });
      fetchOportunidadesDiferidas();
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  
  const handleDescartarOportunidad = async (opId) => {
    try {
      await api.delete(`/oportunidades-diferidas/${opId}`);
      toast.success('Oportunidad descartada');
      fetchOportunidadesDiferidas();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };
  
  // Cargar oportunidades cuando se cambia a esa pestaña
  useEffect(() => {
    if (activeTab === 'oportunidades') {
      fetchOportunidadesDiferidas();
    }
  }, [activeTab]);

  const handleOpenEditor = async (proposal) => {
    setSelectedProposal(proposal);
    // Si tiene precio_uf lo usamos, sino calculamos desde precio_total
    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
    
    // Determinar si usa estructura de pago avanzada
    const tienePagoAvanzado = !!(proposal.primera_cuota_uf || proposal.honorario_resultado_texto || proposal.descuento_pago_anticipado);
    
    setEditForm({
      titulo: proposal.titulo,
      descripcion: proposal.descripcion,
      precio_uf: precioUF.toString(),
      meses_ejecucion: proposal.meses_ejecucion.toString(),
      num_cuotas: (proposal.cuotas?.filter(c => !c.es_cuota_exito)?.length || 1).toString(),
      // Estructura de pago avanzada
      usar_pago_avanzado: tienePagoAvanzado,
      reparto_mode: proposal.reparto_mode || 'iguales',
      primera_cuota_uf: proposal.primera_cuota_uf ? proposal.primera_cuota_uf.toString() : '',
      honorario_resultado_texto: proposal.honorario_resultado_texto || '',
      descuento_pago_anticipado: proposal.descuento_pago_anticipado || false,
      fecha_primera_cuota: '',
      cuotas_personalizadas: [],
      // Inicio diferido de cobranza
      billing_start_mode: proposal.billing_start_mode || 'immediate',
      billing_start_date: proposal.billing_start_date ? proposal.billing_start_date.split('T')[0] : '',
      related_psp_id: proposal.related_psp_id || '',
    });
    
    // Cargar PSPs disponibles para subordinación si el modo es after_related_psp
    if (proposal.billing_start_mode === 'after_related_psp' && proposal.client_id) {
      fetchAvailablePspsForSubordination(proposal.client_id);
    }
    
    // Cargar minutas asociadas a esta PSP
    try {
      const minutasRes = await api.get(`/proposals/${proposal.id}/minutas`);
      setProposalMinutas(minutasRes.data);
    } catch (error) {
      setProposalMinutas([]);
    }
    
    // Cargar ítems de cobro
    setItemsCobro(proposal.items_cobro || []);
    
    // Cargar tareas disponibles de las minutas
    try {
      const tareasRes = await api.get(`/proposals/${proposal.id}/tareas-disponibles`);
      setTareasDisponibles(tareasRes.data);
    } catch (error) {
      setTareasDisponibles([]);
    }
    
    setShowEditor(true);
  };

  // Función para cargar tareas disponibles actualizadas
  const fetchTareasDisponibles = async () => {
    if (!selectedProposal) return;
    setLoadingTareas(true);
    try {
      const tareasRes = await api.get(`/proposals/${selectedProposal.id}/tareas-disponibles`);
      setTareasDisponibles(tareasRes.data);
    } catch (error) {
      setTareasDisponibles([]);
    }
    setLoadingTareas(false);
  };

  // Funciones para ítems de cobro
  const handleAddItemCobro = async () => {
    if (!newItem.tarea_ids || newItem.tarea_ids.length === 0) {
      toast.error('Debe seleccionar al menos una tarea');
      return;
    }
    if (!newItem.valor_uf) {
      toast.error('Debe ingresar un valor en UF');
      return;
    }
    
    // Auto-generar nombre basado en tareas seleccionadas
    const tareasSeleccionadas = tareasDisponibles.filter(t => newItem.tarea_ids.includes(t.id));
    const nombreAuto = newItem.tarea_ids.length === 1 
      ? tareasSeleccionadas[0]?.titulo 
      : `Grupo de ${newItem.tarea_ids.length} tareas`;
    
    try {
      const response = await api.post(`/proposals/${selectedProposal.id}/items-cobro`, {
        tipo: newItem.tarea_ids.length === 1 ? 'tarea' : 'grupo',
        nombre: newItem.nombre || nombreAuto,
        descripcion: newItem.descripcion,
        valor_uf: parseFloat(newItem.valor_uf),
        orden: itemsCobro.length,
        tarea_ids: newItem.tarea_ids
      });
      
      // Actualizar lista local y precio
      const itemsRes = await api.get(`/proposals/${selectedProposal.id}/items-cobro`);
      setItemsCobro(itemsRes.data);
      
      // Actualizar precio en editForm
      setEditForm(prev => ({
        ...prev,
        precio_uf: response.data.nuevo_total_uf.toFixed(2)
      }));
      
      // Actualizar selectedProposal
      setSelectedProposal(prev => ({
        ...prev,
        precio_uf: response.data.nuevo_total_uf,
        precio_total: response.data.nuevo_total_clp,
        items_cobro: itemsRes.data
      }));
      
      setNewItem({ tipo: 'tarea', nombre: '', descripcion: '', valor_uf: '', tarea_ids: [] });
      setShowAddItemModal(false);
      // Recargar tareas disponibles
      await fetchTareasDisponibles();
      toast.success('Ítem de cobro agregado');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleUpdateItemCobro = async () => {
    if (!editingItem) return;
    if (!newItem.tarea_ids || newItem.tarea_ids.length === 0) {
      toast.error('Debe seleccionar al menos una tarea');
      return;
    }
    if (!newItem.valor_uf) {
      toast.error('Debe ingresar un valor en UF');
      return;
    }
    
    // Auto-generar nombre basado en tareas seleccionadas
    const tareasSeleccionadas = tareasDisponibles.filter(t => newItem.tarea_ids.includes(t.id));
    const nombreAuto = newItem.tarea_ids.length === 1 
      ? tareasSeleccionadas[0]?.titulo 
      : `Grupo de ${newItem.tarea_ids.length} tareas`;
    
    try {
      const response = await api.put(`/proposals/${selectedProposal.id}/items-cobro/${editingItem.id}`, {
        tipo: newItem.tarea_ids.length === 1 ? 'tarea' : 'grupo',
        nombre: newItem.nombre || nombreAuto,
        descripcion: newItem.descripcion,
        valor_uf: parseFloat(newItem.valor_uf),
        orden: editingItem.orden,
        tarea_ids: newItem.tarea_ids
      });
      
      // Actualizar lista local
      const itemsRes = await api.get(`/proposals/${selectedProposal.id}/items-cobro`);
      setItemsCobro(itemsRes.data);
      
      // Actualizar precio en editForm
      setEditForm(prev => ({
        ...prev,
        precio_uf: response.data.nuevo_total_uf.toFixed(2)
      }));
      
      setNewItem({ tipo: 'tarea', nombre: '', descripcion: '', valor_uf: '', tarea_ids: [] });
      setEditingItem(null);
      setShowAddItemModal(false);
      // Recargar tareas disponibles
      await fetchTareasDisponibles();
      toast.success('Ítem actualizado');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteItemCobro = async (itemId) => {
    try {
      const response = await api.delete(`/proposals/${selectedProposal.id}/items-cobro/${itemId}`);
      
      // Actualizar lista local
      setItemsCobro(prev => prev.filter(i => i.id !== itemId));
      
      // Actualizar precio en editForm
      setEditForm(prev => ({
        ...prev,
        precio_uf: response.data.nuevo_total_uf.toFixed(2)
      }));
      
      // Recargar tareas disponibles (las tareas vuelven a estar disponibles)
      await fetchTareasDisponibles();
      
      // Cerrar modal
      setShowDeleteItemModal(false);
      setItemToDelete(null);
      
      toast.success('Ítem eliminado');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };
  
  // Función para abrir modal de confirmación de eliminación
  const confirmDeleteItem = (item) => {
    setItemToDelete(item);
    setShowDeleteItemModal(true);
  };

  const openEditItemModal = (item) => {
    setEditingItem(item);
    setNewItem({
      tipo: item.tipo,
      nombre: item.nombre,
      descripcion: item.descripcion || '',
      valor_uf: item.valor_uf.toString(),
      tarea_ids: item.tarea_ids || []
    });
    setShowAddItemModal(true);
  };

  const handleViewMinutaPDF = async (minutaId) => {
    try {
      const response = await api.post(`/meetings/${minutaId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const toggleMinutaSelection = (minutaId) => {
    setSelectedMinutas(prev => 
      prev.includes(minutaId)
        ? prev.filter(id => id !== minutaId)
        : [...prev, minutaId]
    );
  };

  const handleSaveChanges = async () => {
    if (!selectedProposal) return;
    
    setSaving(true);
    try {
      const updateData = {};
      if (editForm.titulo !== selectedProposal.titulo) updateData.titulo = editForm.titulo;
      if (editForm.descripcion !== selectedProposal.descripcion) updateData.descripcion = editForm.descripcion;
      
      const newPrecioUF = parseFloat(editForm.precio_uf);
      const oldPrecioUF = selectedProposal.precio_uf || (selectedProposal.precio_total / UF_TO_CLP);
      if (newPrecioUF !== oldPrecioUF) {
        updateData.precio_uf = parseFloat(editForm.precio_uf);
        updateData.precio_total = ufToCLP(newPrecioUF);
      }
      
      const oldNumCuotas = selectedProposal.cuotas?.filter(c => !c.es_cuota_exito)?.length || 1;
      if (parseInt(editForm.meses_ejecucion) !== selectedProposal.meses_ejecucion) updateData.meses_ejecucion = parseInt(editForm.meses_ejecucion);
      if (parseInt(editForm.num_cuotas) !== oldNumCuotas) updateData.num_cuotas = parseInt(editForm.num_cuotas);
      
      // Estructura de Pago Avanzada
      // Honorario a resultado (texto libre)
      if ((editForm.honorario_resultado_texto || '') !== (selectedProposal.honorario_resultado_texto || '')) {
        updateData.honorario_resultado_texto = editForm.honorario_resultado_texto || null;
      }
      
      // Reparto mode (siempre se procesa, vive en CuotasCalculator)
      updateData.reparto_mode = editForm.reparto_mode || 'iguales';
      
      // Opciones avanzadas de cobranza (descuento, diferido, subordinación)
      if (editForm.usar_pago_avanzado) {
        const newPrimeraCuota = editForm.primera_cuota_uf ? parseFloat(editForm.primera_cuota_uf) : null;
        if (newPrimeraCuota !== selectedProposal.primera_cuota_uf) {
          updateData.primera_cuota_uf = newPrimeraCuota;
        }
        if (editForm.descuento_pago_anticipado !== selectedProposal.descuento_pago_anticipado) {
          updateData.descuento_pago_anticipado = editForm.descuento_pago_anticipado;
        }
      }
      
      // Enviar cuotas personalizadas si fueron editadas
      if (editForm.cuotas_personalizadas?.length > 0) {
        updateData.cuotas_personalizadas = editForm.cuotas_personalizadas;
      }

      if (Object.keys(updateData).length > 0) {
        await api.put(`/proposals/${selectedProposal.id}`, updateData);
        toast.success('Cambios guardados');
        fetchData();
        // Actualizar el proposal seleccionado
        const updatedProposal = await api.get(`/proposals/${selectedProposal.id}`);
        setSelectedProposal(updatedProposal.data);
      } else {
        toast.info('No hay cambios que guardar');
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    // Si es Aceptada, mostrar modal de datos de facturación primero
    if (newStatus === 'Aceptada' || newStatus === 'Ejecución/Activa') {
      const proposal = proposals.find(p => p.id === proposalId) || selectedProposal;
      setProposalToAccept(proposal);
      // Pre-llenar con datos del cliente (datos persistidos previos > datos básicos)
      const client = clients.find(c => c.id === proposal?.client_id);
      if (client) {
        const prevFact = client.datos_facturacion || {};
        const prevContrato = client.datos_contrato || {};
        setDatosFacturacion({
          nombre_razon_social: prevFact.nombre_razon_social || client.company || client.name || '',
          rut: prevFact.rut || client.rut || '',
          contacto_nombre: prevFact.contacto_nombre || client.name || '',
          contacto_email: prevFact.contacto_email || client.email || '',
          contacto_telefono: prevFact.contacto_telefono || client.phone || '',
          notas: prevFact.notas || ''
        });
        setDatosContrato({
          representante_legal: prevContrato.representante_legal || client.name || '',
          rut_representante: prevContrato.rut_representante || client.rut || '',
          domicilio_legal: prevContrato.domicilio_legal || ''
        });
      }
      setUsarMismosDatos(false);
      setShowFacturacionModal(true);
      return;
    }
    
    // PROTECCIÓN: si la PSP ya está activada, advertir antes de degradar el estado
    const proposal = proposals.find(p => p.id === proposalId) || selectedProposal;
    const ESTADOS_POST_ACEPTACION = ['Aceptada', 'Ejecución/Activa'];
    const ESTADOS_PRE_ACEPTACION = ['Borrador', 'En Negociación', 'Enviada', 'Primer Pago'];
    if (
      proposal &&
      ESTADOS_POST_ACEPTACION.includes(proposal.status) &&
      ESTADOS_PRE_ACEPTACION.includes(newStatus)
    ) {
      toast.error(
        `No se puede degradar una PSP desde '${proposal.status}' hacia '${newStatus}'. La propuesta ya generó tareas, alertas y posiblemente facturación. Para anularla usa 'Rechazada' o crea una nueva PSP.`,
        { duration: 6000 }
      );
      return;
    }
    
    try {
      const response = await api.put(`/proposals/${proposalId}/status?status=${encodeURIComponent(newStatus)}`);
      toast.success(response.data.message || `Estado cambiado a ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // ===== FUNCIONES DE CONTROL DE COBRANZA =====
  const handleActivateBilling = async (proposalId) => {
    if (!window.confirm('¿Activar la cobranza de esta PSP? Las cuotas comenzarán a ser exigibles inmediatamente.')) {
      return;
    }
    
    try {
      const response = await api.post(`/proposals/${proposalId}/activate-billing`);
      toast.success(`Cobranza activada para ${response.data.folio}`);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleCompleteBilling = async (proposalId) => {
    if (!window.confirm('¿Marcar la cobranza como completada? Si hay PSPs subordinadas, se activarán automáticamente.')) {
      return;
    }
    
    try {
      const response = await api.post(`/proposals/${proposalId}/complete-billing`);
      const msg = response.data.psps_subordinadas_activadas > 0 
        ? `Cobranza completada para ${response.data.folio}. ${response.data.psps_subordinadas_activadas} PSP(s) subordinada(s) activada(s).`
        : `Cobranza completada para ${response.data.folio}`;
      toast.success(msg);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };
  // ===== FIN CONTROL DE COBRANZA =====

  // Handler para aceptar PSP con datos de facturación
  const handleAcceptWithFacturacion = async () => {
    // Validar campos obligatorios de facturación
    if (!datosFacturacion.nombre_razon_social.trim()) {
      toast.error('El nombre/razón social es obligatorio');
      return;
    }
    if (!datosFacturacion.rut.trim()) {
      toast.error('El RUT es obligatorio');
      return;
    }
    if (!datosFacturacion.contacto_nombre.trim()) {
      toast.error('El nombre del contacto es obligatorio');
      return;
    }
    if (!datosFacturacion.contacto_email.trim()) {
      toast.error('El email del contacto es obligatorio');
      return;
    }
    if (!datosFacturacion.contacto_telefono.trim()) {
      toast.error('El teléfono del contacto es obligatorio');
      return;
    }
    
    // Resolver datos del contrato (auto-completar si checkbox activo)
    const contratoFinal = usarMismosDatos
      ? {
          representante_legal: datosFacturacion.contacto_nombre,
          rut_representante: datosFacturacion.rut,
          domicilio_legal: datosFacturacion.notas || datosContrato.domicilio_legal || ''
        }
      : datosContrato;
    
    // Validar campos obligatorios de contrato
    if (!contratoFinal.representante_legal.trim()) {
      toast.error('El representante legal es obligatorio');
      return;
    }
    if (!contratoFinal.rut_representante.trim()) {
      toast.error('El RUT del representante es obligatorio');
      return;
    }
    if (!contratoFinal.domicilio_legal.trim()) {
      toast.error('El domicilio legal es obligatorio');
      return;
    }
    
    try {
      const response = await api.put(`/proposals/${proposalToAccept.id}/accept`, {
        datos_facturacion: datosFacturacion,
        datos_contrato: contratoFinal
      });
      toast.success(response.data.message || 'Propuesta aceptada exitosamente');
      toast.success('Contrato CPS generado automáticamente', { duration: 4000 });
      setShowFacturacionModal(false);
      setShowEditor(false);
      setSelectedProposal(null);
      setProposalToAccept(null);
      setDatosFacturacion({
        nombre_razon_social: '',
        rut: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: '',
        notas: ''
      });
      setDatosContrato({
        representante_legal: '',
        rut_representante: '',
        domicilio_legal: ''
      });
      setUsarMismosDatos(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Handler para ver CPS
  const handleViewCPS = async (proposalId) => {
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-cps`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Handler para descargar CPS
  const handleDownloadCPS = async (proposalId, folio) => {
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-cps`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CPS_${folio}.pdf`;
      link.click();
      toast.success('Contrato CPS descargado');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // ===== CPS FIRMADO (PDF subido por área comercial) =====
  const handleUploadCPSFirmado = (proposalId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,.pdf';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Debes seleccionar un archivo PDF');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error('El archivo excede 15 MB');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        await api.post(`/proposals/${proposalId}/cps-firmado`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('CPS firmado subido correctamente');
        fetchData();
      } catch (err) {
        toast.error(formatErrorMessage(err));
      }
    };
    input.click();
  };

  const handleViewCPSFirmado = async (proposalId) => {
    try {
      const response = await api.get(`/proposals/${proposalId}/cps-firmado`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error(formatErrorMessage(err));
    }
  };

  const handleDeleteCPSFirmado = async (proposalId) => {
    if (!window.confirm('¿Eliminar el CPS firmado? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/proposals/${proposalId}/cps-firmado`);
      toast.success('CPS firmado eliminado');
      fetchData();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    }
  };



  const handleGeneratePDF = async (proposalId, preview = false) => {
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      if (preview) {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `propuesta_${selectedProposal?.folio || 'PSP'}.pdf`;
        link.click();
      }
      
      toast.success(preview ? 'PDF generado' : 'PDF descargado');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleAddBitacoraEntry = async () => {
    if (!selectedProposal || !newBitacoraEntry.tipo || !newBitacoraEntry.descripcion) {
      toast.error('Complete todos los campos de la bitácora');
      return;
    }
    
    try {
      await api.post(
        `/proposals/${selectedProposal.id}/bitacora?tipo=${encodeURIComponent(newBitacoraEntry.tipo)}&descripcion=${encodeURIComponent(newBitacoraEntry.descripcion)}`
      );
      toast.success('Nota agregada a la bitácora');
      setNewBitacoraEntry({ tipo: '', descripcion: '' });
      
      // Refrescar proposal
      const updatedProposal = await api.get(`/proposals/${selectedProposal.id}`);
      setSelectedProposal(updatedProposal.data);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option?.color || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Aceptada':
      case 'Ejecución/Activa':
        return <CheckCircle className="w-4 h-4" />;
      case 'Rechazada':
        return <XCircle className="w-4 h-4" />;
      case 'Enviada':
        return <Send className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const toggleBitacora = (proposalId) => {
    setExpandedBitacora(prev => ({
      ...prev,
      [proposalId]: !prev[proposalId]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900" data-testid="propuestas-title">
                  Comercial y Marketing — Venta
                </h1>
                <p className="text-sm text-slate-500 mt-1">Generación y gestión de Propuestas de Servicios Profesionales (PSP)</p>
              </div>
            </div>
            <Dialog open={showNewProposal} onOpenChange={setShowNewProposal}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="new-proposal-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva PSP
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Crear Nueva Propuesta</DialogTitle>
                  <DialogDescription>Complete los datos para crear una nueva PSP</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Prospecto/Cliente</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => {
                          setShowNewProspect(!showNewProspect);
                          if (!showNewProspect) {
                            setNewProposal({ ...newProposal, client_id: '' });
                          }
                        }}
                        data-testid="toggle-new-prospect-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {showNewProspect ? 'Cancelar nuevo' : 'Nuevo prospecto'}
                      </Button>
                    </div>
                    {!showNewProspect && (
                      <Select 
                        value={newProposal.client_id} 
                        onValueChange={(value) => {
                          setNewProposal({ ...newProposal, client_id: value });
                          fetchAvailablePspsForSubordination(value);
                        }}
                      >
                        <SelectTrigger data-testid="proposal-client-select">
                          <SelectValue placeholder="Seleccionar prospecto" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name} {client.company ? `(${client.company})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {showNewProspect && (
                      <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Nombre <span className="text-red-500">*</span></Label>
                            <Input
                              value={newProspect.name}
                              onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                              placeholder="Nombre del contacto"
                              data-testid="prospect-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Empresa / Razón Social</Label>
                            <Input
                              value={newProspect.company}
                              onChange={(e) => setNewProspect({ ...newProspect, company: e.target.value })}
                              placeholder="Empresa SpA"
                              data-testid="prospect-company-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">RUT</Label>
                            <Input
                              value={newProspect.rut}
                              onChange={(e) => setNewProspect({ ...newProspect, rut: e.target.value })}
                              placeholder="76.XXX.XXX-X"
                              data-testid="prospect-rut-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Email</Label>
                            <Input
                              type="email"
                              value={newProspect.email}
                              onChange={(e) => setNewProspect({ ...newProspect, email: e.target.value })}
                              placeholder="contacto@empresa.com"
                              data-testid="prospect-email-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Teléfono</Label>
                            <Input
                              value={newProspect.phone}
                              onChange={(e) => setNewProspect({ ...newProspect, phone: e.target.value })}
                              placeholder="+56 9 XXXX XXXX"
                              data-testid="prospect-phone-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Origen <span className="text-red-500">*</span></Label>
                            <Select
                              value={newProspect.origen}
                              onValueChange={(v) => setNewProspect({ ...newProspect, origen: v })}
                            >
                              <SelectTrigger data-testid="prospect-origen-select">
                                <SelectValue placeholder="Origen del prospecto" />
                              </SelectTrigger>
                              <SelectContent>
                                {ORIGEN_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <p className="text-xs text-slate-600">
                          El prospecto se creará al guardar la PSP.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Áreas Responsables */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Áreas responsables
                      <span className="text-xs text-slate-500 font-normal">(una o varias)</span>
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      {AREAS_OPTIONS.map((a) => {
                        const isSelected = (newProposal.areas_responsables || []).includes(a.value);
                        return (
                          <button
                            key={a.value}
                            type="button"
                            onClick={() => {
                              const current = newProposal.areas_responsables || [];
                              const next = isSelected
                                ? current.filter(x => x !== a.value)
                                : [...current, a.value];
                              setNewProposal({ ...newProposal, areas_responsables: next });
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-slate-700 border border-slate-300 hover:border-blue-400'
                            }`}
                            data-testid={`area-toggle-${a.value.replace(/\s/g, '-').toLowerCase()}`}
                          >
                            {isSelected && '✓ '}{a.label}
                          </button>
                        );
                      })}
                    </div>
                    {(!selectedMinutas || selectedMinutas.length === 0) && (
                      <p className="text-xs text-amber-700 mt-1">
                        Si esta PSP no se asocia a una minuta, al aceptarla se creará una tarea por cada área seleccionada con la descripción del servicio.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={newProposal.titulo}
                      onChange={(e) => setNewProposal({ ...newProposal, titulo: e.target.value })}
                      placeholder="Ej: Asesoría Tributaria Anual"
                      data-testid="proposal-title-input"
                    />
                  </div>
                  <div>
                    <Label>Descripción del Servicio</Label>
                    <Textarea
                      value={newProposal.descripcion}
                      onChange={(e) => setNewProposal({ ...newProposal, descripcion: e.target.value })}
                      rows={4}
                      placeholder="Describe los servicios incluidos..."
                      data-testid="proposal-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Precio Total (UF)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newProposal.precio_uf}
                        onChange={(e) => setNewProposal({ ...newProposal, precio_uf: e.target.value })}
                        placeholder="80"
                        data-testid="proposal-price-input"
                      />
                      {newProposal.precio_uf && (
                        <p className="text-xs text-slate-500 mt-1">
                          ≈ {formatCLP(ufToCLP(parseFloat(newProposal.precio_uf)))} CLP
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Meses de Ejecución</Label>
                      <Input
                        type="number"
                        value={newProposal.meses_ejecucion}
                        onChange={(e) => setNewProposal({ ...newProposal, meses_ejecucion: e.target.value })}
                        placeholder="12"
                        data-testid="proposal-months-input"
                      />
                    </div>
                    <div>
                      <Label>Número de Cuotas</Label>
                      <Input
                        type="number"
                        value={newProposal.num_cuotas}
                        onChange={(e) => setNewProposal({ ...newProposal, num_cuotas: e.target.value })}
                        placeholder="12"
                        data-testid="proposal-installments-input"
                      />
                    </div>
                  </div>
                  
                  {/* Calculadora de Cuotas */}
                  {newProposal.precio_uf && newProposal.num_cuotas && (
                    <CuotasCalculator
                      precioUf={newProposal.precio_uf}
                      numCuotas={newProposal.num_cuotas}
                      onCuotasChange={(cuotas) => setNewProposal(prev => ({ ...prev, cuotas_personalizadas: cuotas }))}
                      billingStartMode={newProposal.billing_start_mode}
                      billingStartDate={newProposal.billing_start_date}
                      honorarioResultadoTexto={newProposal.honorario_resultado_texto}
                      onHonorarioResultadoChange={(val) => setNewProposal(prev => ({ ...prev, honorario_resultado_texto: val }))}
                      repartoMode={newProposal.reparto_mode}
                      onRepartoModeChange={(val) => setNewProposal(prev => ({ ...prev, reparto_mode: val }))}
                      primeraCuotaUf={newProposal.primera_cuota_uf}
                      onPrimeraCuotaChange={(val) => setNewProposal(prev => ({ ...prev, primera_cuota_uf: val }))}
                      fechaPrimeraCuota={newProposal.fecha_primera_cuota}
                      onFechaPrimeraCuotaChange={(val) => setNewProposal(prev => ({ ...prev, fecha_primera_cuota: val }))}
                    />
                  )}
                  
                  {/* Opciones avanzadas de cobranza */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div 
                      className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${newProposal.usar_pago_avanzado ? 'bg-amber-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                      onClick={() => setNewProposal({ ...newProposal, usar_pago_avanzado: !newProposal.usar_pago_avanzado })}
                    >
                      <Checkbox
                        checked={newProposal.usar_pago_avanzado}
                        onCheckedChange={(checked) => setNewProposal({ ...newProposal, usar_pago_avanzado: checked })}
                        data-testid="nueva-usar-pago-avanzado-checkbox"
                      />
                      <div className="flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-slate-800">Opciones Avanzadas de Cobranza</span>
                      </div>
                    </div>
                    
                    {newProposal.usar_pago_avanzado && (
                      <div className="p-4 bg-amber-50 border-t border-amber-200 space-y-4">
                        {/* Descuento por Pago Anticipado */}
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={newProposal.descuento_pago_anticipado}
                            onCheckedChange={(checked) => setNewProposal({ ...newProposal, descuento_pago_anticipado: checked })}
                            data-testid="descuento-anticipado-checkbox"
                          />
                          <div>
                            <Label className="text-sm text-amber-800 font-medium">
                              Descuento 15% por Pago Anticipado
                            </Label>
                            <p className="text-xs text-amber-600">
                              Ofrecer descuento si el cliente paga todas las cuotas por adelantado.
                            </p>
                          </div>
                        </div>
                        
                        {/* Inicio Diferido de Cobranza */}
                        <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                          <Checkbox
                            checked={newProposal.billing_start_mode === 'fixed_date'}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setNewProposal({ ...newProposal, billing_start_mode: 'fixed_date', related_psp_id: '' });
                              } else {
                                setNewProposal({ ...newProposal, billing_start_mode: 'immediate', billing_start_date: '' });
                              }
                            }}
                            data-testid="inicio-diferido-checkbox"
                          />
                          <div className="flex-1">
                            <Label className="text-sm text-amber-800 font-medium">Diferir Inicio de Cobranza</Label>
                            <p className="text-xs text-amber-600">La primera cuota comenzará en una fecha futura específica.</p>
                          </div>
                        </div>
                        
                        {newProposal.billing_start_mode === 'fixed_date' && (
                          <div className="ml-7 p-3 bg-amber-50 rounded-lg">
                            <Label className="text-sm text-amber-800">Fecha de inicio de cobranza</Label>
                            <Input
                              type="date"
                              value={newProposal.billing_start_date}
                              onChange={(e) => setNewProposal({ ...newProposal, billing_start_date: e.target.value })}
                              min={new Date().toISOString().split('T')[0]}
                              className="mt-1"
                              data-testid="billing-start-date-input"
                            />
                          </div>
                        )}
                        
                        {/* Cobranza Subordinada */}
                        <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                          <Checkbox
                            checked={newProposal.billing_start_mode === 'after_related_psp'}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setNewProposal({ ...newProposal, billing_start_mode: 'after_related_psp', billing_start_date: '' });
                                if (newProposal.client_id) fetchAvailablePspsForSubordination(newProposal.client_id);
                              } else {
                                setNewProposal({ ...newProposal, billing_start_mode: 'immediate', related_psp_id: '' });
                              }
                            }}
                            data-testid="cobranza-subordinada-checkbox"
                          />
                          <div className="flex-1">
                            <Label className="text-sm text-amber-800 font-medium">Subordinar a otra PSP</Label>
                            <p className="text-xs text-amber-600">La cobranza iniciará al finalizar otra PSP del mismo cliente.</p>
                          </div>
                        </div>
                        
                        {newProposal.billing_start_mode === 'after_related_psp' && (
                          <div className="ml-7 p-3 bg-amber-50 rounded-lg">
                            {!newProposal.client_id ? (
                              <p className="text-xs text-amber-700">Selecciona un cliente primero</p>
                            ) : availablePspsForSubordination.length === 0 ? (
                              <p className="text-xs text-amber-700">No hay PSPs activas para este cliente</p>
                            ) : (
                              <div>
                                <Label className="text-sm text-amber-800">PSP que debe finalizar primero</Label>
                                <Select value={newProposal.related_psp_id} onValueChange={(value) => setNewProposal({ ...newProposal, related_psp_id: value })}>
                                  <SelectTrigger className="mt-1" data-testid="related-psp-select">
                                    <SelectValue placeholder="Seleccionar PSP" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePspsForSubordination.map((psp) => (
                                      <SelectItem key={psp.id} value={psp.id}>
                                        {psp.folio} - {psp.titulo?.substring(0, 30)}...
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Selector de Minutas */}
                  <div>
                    <Label className="mb-2 block">Asociar Minutas (opcional)</Label>
                    <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                      {availableMinutas.filter(m => !m.transferido_operaciones).length === 0 ? (
                        <p className="p-4 text-sm text-slate-500 text-center">No hay minutas disponibles</p>
                      ) : (
                        availableMinutas.filter(m => !m.transferido_operaciones).map((minuta) => (
                          <div 
                            key={minuta.id}
                            className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                          >
                            <Checkbox
                              checked={selectedMinutas.includes(minuta.id)}
                              onCheckedChange={() => toggleMinutaSelection(minuta.id)}
                              data-testid={`select-minuta-${minuta.id}`}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">{minuta.title}</p>
                              <p className="text-xs text-slate-500">
                                {minuta.client_name} • {minuta.tareas_count} tareas
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {selectedMinutas.length > 0 && (
                      <p className="text-xs text-primary mt-2">{selectedMinutas.length} minuta(s) seleccionada(s)</p>
                    )}
                  </div>
                  
                  <Button onClick={handleCreateProposal} className="w-full" data-testid="create-proposal-btn">
                    Crear Propuesta
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Barra de búsqueda */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Buscar por nombre, empresa, RUT, correo o folio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white"
                data-testid="search-proposals-input"
              />
            </div>
          </div>

          {/* Tabs: Borradores, En Negociación, Enviadas, Primer Pago, Rechazadas, Aceptadas, Oportunidades */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-7 mb-6 gap-1 h-auto p-1">
              <TabsTrigger value="borradores" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-borradores">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Borradores</span>
                <span className="lg:hidden">Borr.</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => p.status === 'Borrador')).length})</span>
              </TabsTrigger>
              <TabsTrigger value="negociacion" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-negociacion">
                <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">En Negociación</span>
                <span className="lg:hidden">Neg.</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => p.status === 'En Negociación')).length})</span>
              </TabsTrigger>
              <TabsTrigger value="enviadas" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-enviadas">
                <Send className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Enviadas</span>
                <span className="lg:hidden">Env.</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => p.status === 'Enviada')).length})</span>
              </TabsTrigger>
              <TabsTrigger value="primer_pago" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-primer-pago">
                <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Primer Pago</span>
                <span className="lg:hidden">1° Pago</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => p.status === 'Primer Pago')).length})</span>
              </TabsTrigger>
              <TabsTrigger value="rechazadas" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-rechazadas">
                <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Rechazadas</span>
                <span className="lg:hidden">Rech.</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => p.status === 'Rechazada')).length})</span>
              </TabsTrigger>
              <TabsTrigger value="aceptadas" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-aceptadas">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Aceptadas</span>
                <span className="lg:hidden">Acep.</span>
                <span className="ml-0.5">({filterProposals(proposals.filter(p => ['Aceptada', 'Ejecución/Activa'].includes(p.status))).length})</span>
              </TabsTrigger>
              <TabsTrigger value="oportunidades" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-oportunidades">
                <Lightbulb className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden lg:inline">Oportunidades</span>
                <span className="lg:hidden">Opor.</span>
                <span className="ml-0.5">({oportunidadesDiferidas.length})</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Tab: Borradores */}
            <TabsContent value="borradores">
              <div className="space-y-4">
                {filterProposals(proposals.filter(p => p.status === 'Borrador')).length === 0 ? (
                  <Card className="p-12 text-center border-slate-200">
                    <Briefcase className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-600 mb-2">No hay PSP en borrador</p>
                    <p className="text-sm text-slate-500">Crea una nueva propuesta para comenzar</p>
                  </Card>
                ) : (
                  filterProposals(proposals.filter(p => p.status === 'Borrador')).map((proposal) => {
                    const client = clients.find((c) => c.id === proposal.client_id);
                    const isExpanded = expandedBitacora[proposal.id];
                    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                    const valorCuotaUF = precioUF / (proposal.cuotas?.length || 1);
                    
                    return (
                      <Card key={proposal.id} className="border-slate-200 overflow-hidden" data-testid={`proposal-${proposal.id}`}>
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-sm bg-slate-100 px-3 py-1 rounded text-slate-700">
                                  {proposal.folio}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(proposal.status)}`}>
                                  {getStatusIcon(proposal.status)}
                                  {proposal.status}
                                </span>
                              </div>
                              <h3 className="text-xl font-bold text-slate-900 mb-1">{proposal.titulo}</h3>
                              <p className="text-sm text-slate-600 mb-1">
                                Prospecto: {client?.name || proposal.prospecto_snapshot?.nombre || 'N/A'} {(client?.company || proposal.prospecto_snapshot?.empresa) ? `(${client?.company || proposal.prospecto_snapshot?.empresa})` : ''}
                              </p>
                              {client?.origen && (
                                <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    Origen: {client.origen}
                                  </span>
                                </p>
                              )}
                              <p className="text-sm text-slate-700 mb-4 line-clamp-2">{proposal.descripcion}</p>
                              
                              <div className="grid grid-cols-4 gap-4">
                                <div className="bg-slate-50 p-3 rounded-lg">
                                  <span className="text-xs text-slate-600">Precio Total</span>
                                  <p className="text-lg font-bold text-primary">
                                    {formatUF(precioUF)}
                                  </p>
                                  <p className="text-xs text-slate-500">{formatCLP(proposal.precio_total)}</p>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg">
                                  <span className="text-xs text-slate-600">Duración</span>
                                  <p className="text-lg font-bold text-slate-900">
                                    {proposal.meses_ejecucion} meses
                                  </p>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg">
                                  <span className="text-xs text-slate-600">Cuotas</span>
                                  <p className="text-lg font-bold text-slate-900">
                                    {proposal.cuotas?.filter(c => !c.es_cuota_exito).length || 0}
                                  </p>
                                  {proposal.primera_cuota_uf && (
                                    <p className="text-xs text-amber-600">1ª cuota: {formatUF(proposal.primera_cuota_uf)}</p>
                                  )}
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg">
                                  <span className="text-xs text-slate-600">Valor Cuota</span>
                                  <p className="text-lg font-bold text-primary">
                                    {formatUF(valorCuotaUF)}
                                  </p>
                                  <p className="text-xs text-slate-500">{formatCLP(proposal.cuotas?.[0]?.monto || 0)}</p>
                                </div>
                              </div>
                              
                              {/* Badges de Pago Avanzado */}
                              {(proposal.honorario_resultado_texto || proposal.descuento_pago_anticipado) && (
                                <div className="flex gap-2 mt-3">
                                  {proposal.honorario_resultado_texto && (
                                    <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                                      <DollarSign className="w-3 h-3 mr-1" />
                                      Fee a Resultado
                                    </Badge>
                                  )}
                                  {proposal.descuento_pago_anticipado && (
                                    <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      15% Descuento Anticipado
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              {/* Badge de Estado de Cobranza */}
                              {proposal.billing_start_mode && proposal.billing_start_mode !== 'immediate' && (
                                <div className="flex gap-2 mt-2">
                                  <Badge variant="outline" className={`
                                    ${proposal.billing_status === 'pending_start' 
                                      ? 'border-orange-300 text-orange-700 bg-orange-50' 
                                      : 'border-emerald-300 text-emerald-700 bg-emerald-50'}
                                  `}>
                                    <Clock className="w-3 h-3 mr-1" />
                                    {proposal.billing_status === 'pending_start' ? (
                                      proposal.billing_start_mode === 'fixed_date' 
                                        ? `Cobro desde ${proposal.billing_start_date ? new Date(proposal.billing_start_date).toLocaleDateString('es-CL') : 'fecha definida'}`
                                        : `Cobro al finalizar ${proposal.related_psp_folio || 'PSP relacionada'}`
                                    ) : (
                                      'Cobro activo'
                                    )}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenEditor(proposal)}
                                data-testid={`edit-btn-${proposal.id}`}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGeneratePDF(proposal.id, true)}
                                data-testid={`preview-btn-${proposal.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver PSP
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGeneratePDF(proposal.id, false)}
                                data-testid={`download-btn-${proposal.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Descargar
                              </Button>
                              {['Borrador', 'Enviada', 'En Negociación'].includes(proposal.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  onClick={() => openFragmentacionModal(proposal)}
                                  data-testid={`fragmentar-btn-${proposal.id}`}
                                >
                                  <GitBranch className="w-4 h-4 mr-1" />
                                  Fragmentar
                                </Button>
                              )}
                              {proposal.psp_madre_id && (
                                <div className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                  <Layers className="w-3 h-3" />
                                  Fragmentación de {proposal.psp_madre_folio}
                                </div>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setProposalToDelete(proposal);
                                    setShowDeleteDialog(true);
                                  }}
                                  data-testid={`delete-btn-${proposal.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Eliminar
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Bitácora Preview */}
                        {proposal.bitacora_comercial?.length > 0 && (
                          <div className="border-t border-slate-200">
                            <button
                              onClick={() => toggleBitacora(proposal.id)}
                              className="w-full px-6 py-3 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50"
                            >
                              <span className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                Bitácora ({proposal.bitacora_comercial.length} entradas)
                              </span>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="px-6 pb-4"
                                >
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {proposal.bitacora_comercial.slice().reverse().map((entry, idx) => (
                                      <div key={idx} className="bg-slate-50 p-3 rounded-lg text-sm">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="font-medium text-primary">{entry.tipo}</span>
                                          <span className="text-xs text-slate-500">
                                            {new Date(entry.fecha).toLocaleString('es-CL')}
                                          </span>
                                        </div>
                                        <p className="text-slate-700">{entry.descripcion}</p>
                                        <p className="text-xs text-slate-500 mt-1">Por: {entry.usuario}</p>
                                      </div>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>
            
            {/* Tab: En Negociación */}
            <TabsContent value="negociacion">
              <div className="space-y-4">
                {filterProposals(proposals.filter(p => p.status === 'En Negociación')).length === 0 ? (
                  <Card className="p-12 text-center border-slate-200">
                    <MessageSquare className="w-16 h-16 text-amber-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-600 mb-2">No hay PSP en negociación</p>
                    <p className="text-sm text-slate-500">Las propuestas en proceso de negociación aparecerán aquí</p>
                  </Card>
                ) : (
                  filterProposals(proposals.filter(p => p.status === 'En Negociación')).map((proposal) => {
                    const client = clients.find((c) => c.id === proposal.client_id);
                    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                    const valorCuotaUF = precioUF / (proposal.cuotas?.filter(c => !c.es_cuota_exito)?.length || 1);
                    
                    return (
                      <Card key={proposal.id} className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`proposal-negociacion-${proposal.id}`}>
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-sm bg-slate-100 px-3 py-1 rounded text-slate-700">{proposal.folio}</span>
                                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">En Negociación</Badge>
                              </div>
                              <h3 className="font-semibold text-lg text-slate-900 mb-1">{proposal.titulo}</h3>
                              <p className="text-slate-600 text-sm">
                                <span className="font-medium">Cliente:</span> {client?.prospecto || client?.name || proposal.prospecto_snapshot?.nombre || 'Sin asignar'}
                                {client?.empresa && <span className="ml-2 text-slate-500">({client.empresa})</span>}
                              </p>
                              {client?.origen && (
                                <p className="text-xs mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    Origen: {client.origen}
                                  </span>
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-primary">{formatUF(precioUF)}</p>
                              <p className="text-sm text-slate-500">{formatCLP(proposal.precio_total)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => handleOpenEditor(proposal)}>
                              <Edit2 className="w-4 h-4 mr-1" /> Editar
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleGeneratePDF(proposal.id, true)}>
                              <Eye className="w-4 h-4 mr-1" /> Ver PSP
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              onClick={() => openFragmentacionModal(proposal)}
                              data-testid={`fragmentar-negociacion-btn-${proposal.id}`}
                            >
                              <GitBranch className="w-4 h-4 mr-1" />
                              Fragmentar
                            </Button>
                            {isAdmin && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setProposalToDelete(proposal);
                                  setShowDeleteDialog(true);
                                }}
                                data-testid={`delete-negociacion-btn-${proposal.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>
            
            {/* Tab: Enviadas */}
            <TabsContent value="enviadas">
              <div className="space-y-4">
                {filterProposals(proposals.filter(p => p.status === 'Enviada')).length === 0 ? (
                  <Card className="p-12 text-center border-slate-200">
                    <Send className="w-16 h-16 text-blue-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-600 mb-2">No hay PSP enviadas</p>
                    <p className="text-sm text-slate-500">Las propuestas enviadas al cliente aparecerán aquí</p>
                  </Card>
                ) : (
                  filterProposals(proposals.filter(p => p.status === 'Enviada')).map((proposal) => {
                    const client = clients.find((c) => c.id === proposal.client_id);
                    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                    
                    return (
                      <Card key={proposal.id} className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`proposal-enviada-${proposal.id}`}>
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-sm bg-slate-100 px-3 py-1 rounded text-slate-700">{proposal.folio}</span>
                                <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">Enviada</Badge>
                              </div>
                              <h3 className="font-semibold text-lg text-slate-900 mb-1">{proposal.titulo}</h3>
                              <p className="text-slate-600 text-sm">
                                <span className="font-medium">Cliente:</span> {client?.prospecto || client?.name || proposal.prospecto_snapshot?.nombre || 'Sin asignar'}
                                {client?.empresa && <span className="ml-2 text-slate-500">({client.empresa})</span>}
                              </p>
                              {client?.origen && (
                                <p className="text-xs mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    Origen: {client.origen}
                                  </span>
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-primary">{formatUF(precioUF)}</p>
                              <p className="text-sm text-slate-500">{formatCLP(proposal.precio_total)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => handleOpenEditor(proposal)}>
                              <Edit2 className="w-4 h-4 mr-1" /> Ver Detalles
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleGeneratePDF(proposal.id, true)}>
                              <Eye className="w-4 h-4 mr-1" /> Ver PSP
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              onClick={() => openFragmentacionModal(proposal)}
                              data-testid={`fragmentar-enviada-btn-${proposal.id}`}
                            >
                              <GitBranch className="w-4 h-4 mr-1" />
                              Fragmentar
                            </Button>
                            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => handleStatusChange(proposal.id, 'Primer Pago')} data-testid={`pre-approve-btn-${proposal.id}`}>
                              <DollarSign className="w-4 h-4 mr-1" /> Pre-aprobar (1° Pago)
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleStatusChange(proposal.id, 'Rechazada')}>
                              <XCircle className="w-4 h-4 mr-1" /> Rechazar
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>
            
            {/* Tab: Primer Pago */}
            <TabsContent value="primer_pago">
              <div className="space-y-4">
                {filterProposals(proposals.filter(p => p.status === 'Primer Pago')).length === 0 ? (
                  <Card className="p-12 text-center border-orange-200 bg-orange-50/30" data-testid="empty-primer-pago">
                    <DollarSign className="w-16 h-16 text-orange-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-700 mb-2">No hay PSPs pendientes de primer pago</p>
                    <p className="text-sm text-slate-500">Cuando un cliente pre-apruebe una propuesta, aparecerá aquí esperando confirmación del pago.</p>
                  </Card>
                ) : (
                  <>
                    {/* Resumen agregado */}
                    {(() => {
                      const list = filterProposals(proposals.filter(p => p.status === 'Primer Pago'));
                      const totalUf = list.reduce((acc, p) => acc + (p.primera_cuota_uf || ((p.cuotas?.[0]?.monto || 0) / UF_TO_CLP) || (p.precio_uf || 0) / (p.num_cuotas || 1)), 0);
                      const totalClp = list.reduce((acc, p) => acc + ((p.cuotas?.[0]?.monto) || ((p.precio_total || 0) / (p.num_cuotas || 1))), 0);
                      return (
                        <Card className="p-4 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="primer-pago-summary">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="bg-orange-500 text-white p-2 rounded-lg">
                                <DollarSign className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">Dinero por entrar</p>
                                <p className="text-xs text-orange-600">{list.length} PSP{list.length !== 1 ? 's' : ''} en pre-aprobación</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-orange-700">{totalUf.toFixed(2)} UF</p>
                              <p className="text-sm text-orange-600">{formatCLP(totalClp)}</p>
                            </div>
                          </div>
                        </Card>
                      );
                    })()}
                    
                    {filterProposals(proposals.filter(p => p.status === 'Primer Pago')).map((proposal) => {
                      const client = clients.find((c) => c.id === proposal.client_id);
                      const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                      const primeraCuota = proposal.cuotas?.[0];
                      const primeraCuotaCLP = primeraCuota?.monto || (proposal.precio_total / (proposal.num_cuotas || 1));
                      const primeraCuotaUF = primeraCuota?.monto_uf || (primeraCuotaCLP / UF_TO_CLP);
                      const rutFacturacion = proposal.datos_facturacion?.rut || client?.rut || 'No registrado';
                      const nombreFacturacion = proposal.datos_facturacion?.nombre_razon_social || client?.company || client?.name || '—';
                      
                      return (
                        <Card key={proposal.id} className="border-orange-300 overflow-hidden hover:shadow-md transition-shadow border-l-4 border-l-orange-500" data-testid={`proposal-primer-pago-${proposal.id}`}>
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="font-mono text-sm bg-slate-100 px-3 py-1 rounded text-slate-700">{proposal.folio}</span>
                                  <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                                    <DollarSign className="w-3 h-3 mr-1" /> Primer Pago
                                  </Badge>
                                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                                    Pre-aprobada
                                  </Badge>
                                </div>
                                <h3 className="font-semibold text-lg text-slate-900 mb-1">{proposal.titulo}</h3>
                                <p className="text-slate-600 text-sm">
                                  <span className="font-medium">Cliente:</span> {client?.prospecto || client?.name || proposal.prospecto_snapshot?.nombre || 'Sin asignar'}
                                  {client?.empresa && <span className="ml-2 text-slate-500">({client.empresa})</span>}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-slate-500">Total PSP</p>
                                <p className="text-xl font-bold text-slate-700">{formatUF(precioUF)}</p>
                              </div>
                            </div>
                            
                            {/* Datos destacados: 1ª cuota + RUT */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid={`primera-cuota-${proposal.id}`}>
                                <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold mb-1">Monto 1ª Cuota</p>
                                <p className="text-2xl font-bold text-orange-700">{primeraCuotaUF.toFixed(2)} UF</p>
                                <p className="text-sm text-orange-600">{formatCLP(primeraCuotaCLP)}</p>
                              </div>
                              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid={`rut-facturacion-${proposal.id}`}>
                                <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1">RUT Facturación</p>
                                <p className="text-lg font-mono font-semibold text-slate-800">{rutFacturacion}</p>
                                <p className="text-sm text-slate-600 truncate">{nombreFacturacion}</p>
                              </div>
                            </div>
                            
                            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-xs text-amber-800">
                              <strong>Importante:</strong> Esta PSP NO genera CPS ni libera tareas al Tablero de Líderes hasta confirmar el pago.
                            </div>
                            
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleOpenEditor(proposal)} data-testid={`view-details-pp-${proposal.id}`}>
                                <Edit2 className="w-4 h-4 mr-1" /> Ver Detalles
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleGeneratePDF(proposal.id, true)}>
                                <Eye className="w-4 h-4 mr-1" /> Ver PSP
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                                onClick={() => handleStatusChange(proposal.id, 'Aceptada')}
                                data-testid={`confirm-payment-btn-${proposal.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Confirmar Pago Recibido
                              </Button>
                              <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50 ml-auto" onClick={() => handleStatusChange(proposal.id, 'Enviada')} data-testid={`return-sent-btn-${proposal.id}`}>
                                Volver a Enviada
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleStatusChange(proposal.id, 'Rechazada')}>
                                <XCircle className="w-4 h-4 mr-1" /> Rechazar
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </>
                )}
              </div>
            </TabsContent>
            
            {/* Tab: Rechazadas */}
            <TabsContent value="rechazadas">
              <div className="space-y-4">
                {filterProposals(proposals.filter(p => p.status === 'Rechazada')).length === 0 ? (
                  <Card className="p-12 text-center border-slate-200">
                    <XCircle className="w-16 h-16 text-red-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-600 mb-2">No hay PSP rechazadas</p>
                    <p className="text-sm text-slate-500">Las propuestas rechazadas por el cliente aparecerán aquí</p>
                  </Card>
                ) : (
                  filterProposals(proposals.filter(p => p.status === 'Rechazada')).map((proposal) => {
                    const client = clients.find((c) => c.id === proposal.client_id);
                    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                    
                    return (
                      <Card key={proposal.id} className="border-slate-200 overflow-hidden opacity-75 hover:opacity-100 transition-opacity" data-testid={`proposal-rechazada-${proposal.id}`}>
                        <div className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-sm bg-slate-100 px-3 py-1 rounded text-slate-700">{proposal.folio}</span>
                                <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">Rechazada</Badge>
                              </div>
                              <h3 className="font-semibold text-lg text-slate-900 mb-1">{proposal.titulo}</h3>
                              <p className="text-slate-600 text-sm">
                                <span className="font-medium">Cliente:</span> {client?.prospecto || client?.name || proposal.prospecto_snapshot?.nombre || 'Sin asignar'}
                                {client?.empresa && <span className="ml-2 text-slate-500">({client.empresa})</span>}
                              </p>
                              {client?.origen && (
                                <p className="text-xs mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    Origen: {client.origen}
                                  </span>
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-slate-400 line-through">{formatUF(precioUF)}</p>
                              <p className="text-sm text-slate-400">{formatCLP(proposal.precio_total)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => handleGeneratePDF(proposal.id, true)}>
                              <Eye className="w-4 h-4 mr-1" /> Ver PSP
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleStatusChange(proposal.id, 'Borrador')}>
                              <Edit2 className="w-4 h-4 mr-1" /> Reactivar como Borrador
                            </Button>
                            {/* Solo Administradores (Socios) pueden eliminar PSP Rechazadas */}
                            {isAdmin && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  setProposalToDelete(proposal);
                                  setShowDeleteDialog(true);
                                }}
                                data-testid={`delete-rechazada-btn-${proposal.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>
            
            {/* Tab: PSP Aceptadas — agrupadas por cliente, colapsadas por defecto, búsqueda dedicada */}
            <TabsContent value="aceptadas">
              {(() => {
                const aceptadas = filterProposals(proposals.filter(p => ['Aceptada', 'Ejecución/Activa'].includes(p.status)));
                const localQ = aceptadasSearch.trim().toLowerCase();
                const matchProposal = (p) => {
                  if (!localQ) return true;
                  const c = clients.find(cc => cc.id === p.client_id);
                  return (
                    (p.folio || '').toLowerCase().includes(localQ) ||
                    (p.titulo || '').toLowerCase().includes(localQ) ||
                    (c?.name || '').toLowerCase().includes(localQ) ||
                    (c?.company || '').toLowerCase().includes(localQ) ||
                    (c?.rut || '').toLowerCase().includes(localQ)
                  );
                };
                const filtered = aceptadas.filter(matchProposal);

                // Agrupar por cliente
                const grupos = new Map();
                filtered.forEach(p => {
                  const cid = p.client_id || '__sin_cliente__';
                  if (!grupos.has(cid)) grupos.set(cid, []);
                  grupos.get(cid).push(p);
                });

                // Ordenar PSPs dentro de cada cliente (más nuevas primero) y obtener fecha referencia
                const fechaRef = (p) => p.fecha_aceptacion || p.updated_at || p.created_at || '';
                const grupoList = Array.from(grupos.entries()).map(([cid, list]) => {
                  list.sort((a, b) => String(fechaRef(b)).localeCompare(String(fechaRef(a))));
                  const cliente = clients.find(c => c.id === cid) || {};
                  const ultimaFecha = list[0] ? fechaRef(list[0]) : '';
                  const montoTotalUF = list.reduce((sum, p) => sum + (p.precio_uf || (p.precio_total / UF_TO_CLP) || 0), 0);
                  return { cid, cliente, list, ultimaFecha, montoTotalUF };
                });
                // Ordenar clientes por fecha más reciente desc
                grupoList.sort((a, b) => String(b.ultimaFecha).localeCompare(String(a.ultimaFecha)));

                const toggleCliente = (cid) => setExpandedAceptadasClientes(prev => ({ ...prev, [cid]: !prev[cid] }));
                const togglePSP = (pid) => setExpandedAceptadasPSPs(prev => ({ ...prev, [pid]: !prev[pid] }));
                const expandAll = () => {
                  const nc = {}; grupoList.forEach(g => { nc[g.cid] = true; });
                  const np = {}; filtered.forEach(p => { np[p.id] = true; });
                  setExpandedAceptadasClientes(nc);
                  setExpandedAceptadasPSPs(np);
                };
                const collapseAll = () => {
                  setExpandedAceptadasClientes({});
                  setExpandedAceptadasPSPs({});
                };

                return (
                  <div className="space-y-4" data-testid="aceptadas-container">
                    {/* Barra de búsqueda + acciones */}
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Buscar por número de PSP, cliente, empresa o RUT…"
                          className="pl-9 bg-white border-slate-200 focus-visible:ring-slate-400"
                          value={aceptadasSearch}
                          onChange={(e) => setAceptadasSearch(e.target.value)}
                          data-testid="aceptadas-search-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="border-slate-200 text-slate-600 hover:bg-slate-50" onClick={expandAll} data-testid="aceptadas-expand-all">
                          <ChevronDown className="w-4 h-4 mr-1" /> Expandir todo
                        </Button>
                        <Button variant="outline" size="sm" className="border-slate-200 text-slate-600 hover:bg-slate-50" onClick={collapseAll} data-testid="aceptadas-collapse-all">
                          <ChevronUp className="w-4 h-4 mr-1" /> Colapsar todo
                        </Button>
                      </div>
                    </div>

                    {/* Resumen */}
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span><strong className="text-slate-700">{grupoList.length}</strong> cliente{grupoList.length === 1 ? '' : 's'}</span>
                      <span className="w-px h-3 bg-slate-300" />
                      <span><strong className="text-slate-700">{filtered.length}</strong> PSP{filtered.length === 1 ? '' : 's'} aceptada{filtered.length === 1 ? '' : 's'}</span>
                      {aceptadasSearch && (
                        <>
                          <span className="w-px h-3 bg-slate-300" />
                          <button className="text-slate-500 hover:text-slate-700 underline" onClick={() => setAceptadasSearch('')}>
                            limpiar búsqueda
                          </button>
                        </>
                      )}
                    </div>

                    {filtered.length === 0 ? (
                      <Card className="p-12 text-center border-slate-200 bg-white">
                        <FileSignature className="w-14 h-14 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                        <p className="text-base text-slate-600 mb-1">
                          {aceptadas.length === 0 ? 'No hay PSP aceptadas' : 'Sin resultados para tu búsqueda'}
                        </p>
                        <p className="text-sm text-slate-400">
                          {aceptadas.length === 0
                            ? 'Las propuestas aceptadas aparecerán aquí agrupadas por cliente.'
                            : 'Prueba con otro número de PSP, cliente o empresa.'}
                        </p>
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {grupoList.map(({ cid, cliente, list, ultimaFecha, montoTotalUF }) => {
                          const isOpen = !!expandedAceptadasClientes[cid];
                          const tieneFirmados = list.filter(p => p.cps_firmado).length;
                          return (
                            <Card key={cid} className="border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`grupo-cliente-${cid}`}>
                              {/* Cabecera cliente */}
                              <button
                                type="button"
                                onClick={() => toggleCliente(cid)}
                                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                                data-testid={`grupo-cliente-toggle-${cid}`}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isOpen ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-base font-semibold text-slate-900 truncate">
                                      {cliente.name || 'Sin cliente'}
                                    </h3>
                                    {cliente.company && (
                                      <span className="text-xs text-slate-500">· {cliente.company}</span>
                                    )}
                                    {cliente.rut && (
                                      <Badge variant="outline" className="border-slate-200 text-slate-500 bg-slate-50 text-[10px] font-mono">
                                        {cliente.rut}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                                    <span>{list.length} PSP{list.length === 1 ? '' : 's'} aceptada{list.length === 1 ? '' : 's'}</span>
                                    <span className="w-px h-3 bg-slate-300" />
                                    <span>{formatUF(montoTotalUF)} total</span>
                                    {tieneFirmados > 0 && (
                                      <>
                                        <span className="w-px h-3 bg-slate-300" />
                                        <span className="inline-flex items-center gap-1 text-emerald-700">
                                          <FileCheck2 className="w-3 h-3" />
                                          {tieneFirmados}/{list.length} firmado{tieneFirmados === 1 ? '' : 's'}
                                        </span>
                                      </>
                                    )}
                                    {ultimaFecha && (
                                      <>
                                        <span className="w-px h-3 bg-slate-300" />
                                        <span>Última: {new Date(ultimaFecha).toLocaleDateString('es-CL')}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </button>

                              {/* Lista PSPs del cliente */}
                              {isOpen && (
                                <div className="border-t border-slate-100 divide-y divide-slate-100 bg-slate-50/40">
                                  {list.map((proposal) => {
                                    const pspOpen = !!expandedAceptadasPSPs[proposal.id];
                                    const precioUF = proposal.precio_uf || (proposal.precio_total / UF_TO_CLP);
                                    const valorCuotaUF = precioUF / (proposal.cuotas?.length || 1);
                                    return (
                                      <div key={proposal.id} data-testid={`proposal-aceptada-${proposal.id}`}>
                                        {/* Fila colapsada */}
                                        <button
                                          type="button"
                                          onClick={() => togglePSP(proposal.id)}
                                          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white transition-colors text-left"
                                          data-testid={`proposal-aceptada-toggle-${proposal.id}`}
                                        >
                                          <div className={`w-6 h-6 rounded-md flex items-center justify-center ${pspOpen ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-500'}`}>
                                            {pspOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                          </div>
                                          <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                            {proposal.folio}
                                          </span>
                                          <span className="text-sm font-medium text-slate-800 truncate flex-1">
                                            {proposal.titulo}
                                          </span>
                                          <Badge variant="outline" className={`text-[10px] ${proposal.status === 'Ejecución/Activa' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-600 bg-white'}`}>
                                            {proposal.status}
                                          </Badge>
                                          {proposal.cps_firmado ? (
                                            <Badge className="bg-emerald-600/90 hover:bg-emerald-600 text-white text-[10px]">
                                              <FileCheck2 className="w-3 h-3 mr-0.5" /> Firmado
                                            </Badge>
                                          ) : (
                                            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 text-[10px]">
                                              <AlertTriangle className="w-3 h-3 mr-0.5" /> Sin firmar
                                            </Badge>
                                          )}
                                          <span className="text-xs font-semibold text-slate-700 tabular-nums">{formatUF(precioUF)}</span>
                                          <span className="text-[11px] text-slate-400 hidden md:inline whitespace-nowrap">
                                            {proposal.fecha_aceptacion ? new Date(proposal.fecha_aceptacion).toLocaleDateString('es-CL') : (proposal.updated_at ? new Date(proposal.updated_at).toLocaleDateString('es-CL') : '')}
                                          </span>
                                        </button>

                                        {/* Detalle expandido */}
                                        {pspOpen && (
                                          <div className="px-5 pb-5 pt-1 bg-white border-t border-slate-100">
                                            {/* Toolbar */}
                                            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`acciones-aceptada-${proposal.id}`}>
                                              <Button
                                                className="bg-slate-900 hover:bg-slate-800 text-white"
                                                size="sm"
                                                onClick={() => handleViewCPS(proposal.id)}
                                                data-testid={`view-cps-btn-${proposal.id}`}
                                              >
                                                <FileSignature className="w-4 h-4 mr-1" />
                                                Ver Contrato CPS
                                              </Button>

                                              {proposal.cps_firmado ? (
                                                <>
                                                  <Button
                                                    size="sm"
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                    onClick={() => handleViewCPSFirmado(proposal.id)}
                                                    data-testid={`view-cps-firmado-btn-${proposal.id}`}
                                                  >
                                                    <FileCheck2 className="w-4 h-4 mr-1" />
                                                    Ver CPS Firmado
                                                  </Button>
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                                    onClick={() => handleUploadCPSFirmado(proposal.id)}
                                                    data-testid={`reemplazar-cps-firmado-btn-${proposal.id}`}
                                                  >
                                                    <Upload className="w-4 h-4 mr-1" />
                                                    Reemplazar
                                                  </Button>
                                                  {isAdmin && (
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                                      onClick={() => handleDeleteCPSFirmado(proposal.id)}
                                                      data-testid={`eliminar-cps-firmado-btn-${proposal.id}`}
                                                    >
                                                      <Trash2 className="w-4 h-4 mr-1" />
                                                      Eliminar firmado
                                                    </Button>
                                                  )}
                                                </>
                                              ) : (
                                                <Button
                                                  size="sm"
                                                  className="bg-amber-500 hover:bg-amber-600 text-white"
                                                  onClick={() => handleUploadCPSFirmado(proposal.id)}
                                                  data-testid={`subir-cps-firmado-btn-${proposal.id}`}
                                                >
                                                  <Upload className="w-4 h-4 mr-1" />
                                                  Subir CPS Firmado
                                                </Button>
                                              )}

                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-slate-200 text-slate-600 hover:bg-slate-50"
                                                onClick={() => handleGeneratePDF(proposal.id, true)}
                                                data-testid={`preview-psp-btn-${proposal.id}`}
                                              >
                                                <Eye className="w-4 h-4 mr-1" />
                                                Ver PSP Original
                                              </Button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-slate-200 text-slate-600 hover:bg-slate-50"
                                                onClick={() => handleOpenEditor(proposal)}
                                                data-testid={`edit-aceptada-btn-${proposal.id}`}
                                              >
                                                <Edit2 className="w-4 h-4 mr-1" />
                                                Ver Detalles
                                              </Button>
                                              {proposal.datos_facturacion && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                                  onClick={() => {
                                                    setViewingFacturacion(proposal);
                                                    setShowViewFacturacionModal(true);
                                                  }}
                                                  data-testid={`view-facturacion-btn-${proposal.id}`}
                                                >
                                                  <DollarSign className="w-4 h-4 mr-1" />
                                                  Datos Facturación
                                                </Button>
                                              )}
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                                onClick={() => {
                                                  setSelectedPSPAnexo(proposal);
                                                  setShowAnexosModal(true);
                                                }}
                                                data-testid={`crear-anexo-btn-${proposal.id}`}
                                              >
                                                <FilePlus className="w-4 h-4 mr-1" />
                                                Crear Anexo
                                              </Button>
                                              {proposal.billing_status === 'pending_start' && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                                  onClick={() => handleActivateBilling(proposal.id)}
                                                  data-testid={`activate-billing-btn-${proposal.id}`}
                                                >
                                                  <Play className="w-4 h-4 mr-1" />
                                                  Activar Cobranza
                                                </Button>
                                              )}
                                              {proposal.billing_status === 'active' && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="text-sky-700 border-sky-200 hover:bg-sky-50"
                                                  onClick={() => handleCompleteBilling(proposal.id)}
                                                  data-testid={`complete-billing-btn-${proposal.id}`}
                                                >
                                                  <CheckCircle className="w-4 h-4 mr-1" />
                                                  Completar Cobranza
                                                </Button>
                                              )}
                                              {isAdmin && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="text-rose-600 border-rose-200 hover:bg-rose-50 ml-auto"
                                                  onClick={() => {
                                                    setProposalToDelete(proposal);
                                                    setShowDeleteDialog(true);
                                                  }}
                                                  data-testid={`delete-aceptada-btn-${proposal.id}`}
                                                >
                                                  <Trash2 className="w-4 h-4 mr-1" />
                                                  Eliminar PSP/CPS
                                                </Button>
                                              )}
                                            </div>

                                            {/* Grid de datos */}
                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Precio Total</span>
                                                <p className="text-base font-semibold text-slate-900 mt-0.5">{formatUF(precioUF)}</p>
                                                <p className="text-[11px] text-slate-400">{formatCLP(proposal.precio_total)}</p>
                                              </div>
                                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Duración</span>
                                                <p className="text-base font-semibold text-slate-900 mt-0.5">{proposal.meses_ejecucion} meses</p>
                                              </div>
                                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Cuotas</span>
                                                <p className="text-base font-semibold text-slate-900 mt-0.5">{proposal.cuotas?.length || 0}</p>
                                              </div>
                                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Valor Cuota</span>
                                                <p className="text-base font-semibold text-slate-900 mt-0.5">{formatUF(valorCuotaUF)}</p>
                                                <p className="text-[11px] text-slate-400">{formatCLP(proposal.cuotas?.[0]?.monto || 0)}</p>
                                              </div>
                                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Fecha Aceptación</span>
                                                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                                                  {proposal.fecha_aceptacion ? new Date(proposal.fecha_aceptacion).toLocaleDateString('es-CL') : new Date(proposal.updated_at || proposal.created_at).toLocaleDateString('es-CL')}
                                                </p>
                                              </div>
                                            </div>

                                            {/* Cobranza diferida */}
                                            {proposal.billing_start_mode && proposal.billing_start_mode !== 'immediate' && (
                                              <div className="mt-3">
                                                <Badge variant="outline" className={`
                                                  ${proposal.billing_status === 'pending_start'
                                                    ? 'border-amber-200 text-amber-700 bg-amber-50'
                                                    : proposal.billing_status === 'completed'
                                                      ? 'border-sky-200 text-sky-700 bg-sky-50'
                                                      : 'border-emerald-200 text-emerald-700 bg-emerald-50'}
                                                `}>
                                                  <Clock className="w-3 h-3 mr-1" />
                                                  {proposal.billing_status === 'pending_start' ? (
                                                    proposal.billing_start_mode === 'fixed_date'
                                                      ? `Cobro desde ${proposal.billing_start_date ? new Date(proposal.billing_start_date).toLocaleDateString('es-CL') : 'fecha definida'}`
                                                      : `Cobro al finalizar ${proposal.related_psp_folio || 'PSP relacionada'}`
                                                  ) : proposal.billing_status === 'completed' ? (
                                                    'Cobranza completada'
                                                  ) : (
                                                    'Cobro activo'
                                                  )}
                                                </Badge>
                                              </div>
                                            )}

                                            {/* CPS Firmado info */}
                                            {proposal.cps_firmado && (
                                              <p className="text-xs text-slate-500 mt-3">
                                                CPS firmado subido por <strong className="text-slate-700">{proposal.cps_firmado.uploaded_by_name || 'Usuario'}</strong>
                                                {proposal.cps_firmado.uploaded_at && (
                                                  <> el {new Date(proposal.cps_firmado.uploaded_at).toLocaleDateString('es-CL')}</>
                                                )}
                                                {proposal.cps_firmado.size_bytes && (
                                                  <> · {(proposal.cps_firmado.size_bytes / 1024).toFixed(1)} KB</>
                                                )}
                                              </p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </TabsContent>
            
            {/* Tab: Oportunidades Futuras */}
            <TabsContent value="oportunidades">
              <div className="space-y-4">
                {/* Header con botón de crear PSP desde seleccionadas */}
                {selectedOportunidades.length > 0 && (
                  <Card className="p-4 border-primary bg-primary/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-primary" />
                        <span className="font-medium text-primary">
                          {selectedOportunidades.length} oportunidad(es) seleccionada(s)
                        </span>
                      </div>
                      <Button 
                        onClick={() => setShowConvertirModal(true)}
                        className="flex items-center gap-2"
                        data-testid="btn-convertir-oportunidades"
                      >
                        <ArrowRight className="w-4 h-4" />
                        Crear PSP desde selección
                      </Button>
                    </div>
                  </Card>
                )}
                
                {loadingOportunidades ? (
                  <Card className="p-12 text-center border-slate-200">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="text-slate-500 mt-4">Cargando oportunidades...</p>
                  </Card>
                ) : oportunidadesDiferidas.length === 0 ? (
                  <Card className="p-12 text-center border-slate-200">
                    <Lightbulb className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-lg text-slate-600 mb-2">No hay oportunidades diferidas</p>
                    <p className="text-sm text-slate-500">
                      Las tareas que se excluyen al crear o fragmentar una PSP aparecerán aquí como oportunidades comerciales futuras.
                    </p>
                  </Card>
                ) : (
                  oportunidadesDiferidas.map((oportunidad) => (
                    <Card 
                      key={oportunidad.id} 
                      className={`border overflow-hidden transition-all ${
                        selectedOportunidades.includes(oportunidad.id) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`oportunidad-${oportunidad.id}`}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          {/* Checkbox de selección */}
                          <Checkbox
                            checked={selectedOportunidades.includes(oportunidad.id)}
                            onCheckedChange={() => handleSelectOportunidad(oportunidad.id)}
                            className="mt-1"
                            data-testid={`select-oportunidad-${oportunidad.id}`}
                          />
                          
                          <div className="flex-1 min-w-0">
                            {/* Header con CLIENTE como título principal */}
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <div>
                                {/* CLIENTE como encabezado principal */}
                                <div className="flex items-center gap-2 mb-1">
                                  <Building className="w-4 h-4 text-slate-500" />
                                  <h3 className="text-lg font-semibold text-slate-900">
                                    {oportunidad.client_name || oportunidad.client_company || 'Cliente no especificado'}
                                  </h3>
                                  {oportunidad.client_company && oportunidad.client_name && (
                                    <span className="text-sm text-slate-500">
                                      ({oportunidad.client_company})
                                    </span>
                                  )}
                                </div>
                                
                                {/* Nombre de la tarea/servicio como subtítulo */}
                                <p className="text-base font-medium text-slate-700 mb-2 flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-blue-500" />
                                  {oportunidad.titulo}
                                </p>
                                
                                {/* Badges de área, estado y prioridad */}
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                  {oportunidad.area && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
                                      <Tag className="w-3 h-3" />
                                      {oportunidad.area}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                                    oportunidad.status === 'Pendiente Comercial' 
                                      ? 'bg-amber-100 text-amber-700'
                                      : oportunidad.status === 'En Seguimiento'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-slate-100 text-slate-700'
                                  }`}>
                                    {oportunidad.status}
                                  </span>
                                  {oportunidad.prioridad === 1 && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">
                                      Urgente
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Acciones */}
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedOportunidades([oportunidad.id]);
                                    setConvertirForm({
                                      titulo: oportunidad.titulo,
                                      descripcion: oportunidad.descripcion || '',
                                      precio_uf: '',
                                      tipo_servicio: 'Spot',
                                      meses_ejecucion: 3,
                                      num_cuotas: 1
                                    });
                                    setShowConvertirModal(true);
                                  }}
                                  data-testid={`btn-convertir-${oportunidad.id}`}
                                >
                                  <ArrowRight className="w-4 h-4 mr-1" />
                                  Crear PSP
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDescartarOportunidad(oportunidad.id)}
                                  data-testid={`btn-descartar-${oportunidad.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Descripción */}
                            {oportunidad.descripcion && (
                              <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                                {oportunidad.descripcion}
                              </p>
                            )}
                            
                            {/* Trazabilidad: Minuta y PSP origen */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-2">
                              {oportunidad.minuta_folio && (
                                <span className="flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  Minuta: {oportunidad.minuta_folio}
                                </span>
                              )}
                              {oportunidad.psp_origen_folio && (
                                <span className="flex items-center gap-1">
                                  <FileText className="w-3 h-3 text-blue-500" />
                                  PSP Origen: {oportunidad.psp_origen_folio}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(oportunidad.created_at).toLocaleDateString('es-CL')}
                              </span>
                            </div>
                            
                            {/* Motivo de exclusión */}
                            {oportunidad.motivo_exclusion && (
                              <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                                <strong>Motivo:</strong> {oportunidad.motivo_exclusion}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Editor Modal */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Editor de PSP - {selectedProposal?.folio}
            </DialogTitle>
            <DialogDescription>
              Edite los detalles de la propuesta y gestione su estado
            </DialogDescription>
          </DialogHeader>
          
          {selectedProposal && (
            <Tabs defaultValue="detalles" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="detalles">Detalles</TabsTrigger>
                <TabsTrigger value="minutas">Minutas ({proposalMinutas.length})</TabsTrigger>
                <TabsTrigger value="estado">Estado y Acciones</TabsTrigger>
                <TabsTrigger value="bitacora">Bitácora</TabsTrigger>
              </TabsList>
              
              {/* Detalles Tab */}
              <TabsContent value="detalles" className="space-y-4 mt-4">
                <div>
                  <Label>Título</Label>
                  <Input
                    value={editForm.titulo}
                    onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })}
                    data-testid="edit-titulo-input"
                  />
                </div>
                
                <div>
                  <Label>Descripción del Servicio</Label>
                  <Textarea
                    value={editForm.descripcion}
                    onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                    rows={6}
                    data-testid="edit-descripcion-input"
                  />
                </div>
                
                {/* Alerta para PSPs Aceptadas/Activas */}
                {['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status) && (
                  <Alert className="border-amber-300 bg-amber-50">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <strong>Contrato Activo:</strong> Las modificaciones de precio, cuotas y plazos deben realizarse mediante Anexos Contractuales para mantener la trazabilidad.
                      <Button
                        variant="link"
                        className="text-amber-700 underline p-0 h-auto ml-2"
                        onClick={() => {
                          setSelectedPSPAnexo(selectedProposal);
                          setShowAnexosModal(true);
                        }}
                      >
                        Crear Anexo
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Precio Total (UF)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.precio_uf}
                      onChange={(e) => setEditForm({ ...editForm, precio_uf: e.target.value })}
                      data-testid="edit-precio-input"
                      disabled={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status)}
                      className={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status) ? 'bg-slate-100 cursor-not-allowed' : ''}
                    />
                    {editForm.precio_uf && (
                      <p className="text-xs text-slate-500 mt-1">
                        ≈ {formatCLP(ufToCLP(parseFloat(editForm.precio_uf)))} CLP
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Meses de Ejecución</Label>
                    <Input
                      type="number"
                      value={editForm.meses_ejecucion}
                      onChange={(e) => setEditForm({ ...editForm, meses_ejecucion: e.target.value })}
                      data-testid="edit-meses-input"
                      disabled={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status)}
                      className={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status) ? 'bg-slate-100 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label>Número de Cuotas</Label>
                    <Input
                      type="number"
                      value={editForm.num_cuotas}
                      onChange={(e) => setEditForm({ ...editForm, num_cuotas: e.target.value })}
                      data-testid="edit-cuotas-input"
                      disabled={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status)}
                      className={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status) ? 'bg-slate-100 cursor-not-allowed' : ''}
                    />
                  </div>
                </div>
                
                {/* Calculadora de Cuotas */}
                {editForm.precio_uf && editForm.num_cuotas && (
                  <CuotasCalculator
                    precioUf={editForm.precio_uf}
                    numCuotas={editForm.num_cuotas}
                    onCuotasChange={(cuotas) => setEditForm(prev => ({ ...prev, cuotas_personalizadas: cuotas }))}
                    billingStartMode={editForm.billing_start_mode}
                    billingStartDate={editForm.billing_start_date}
                    honorarioResultadoTexto={editForm.honorario_resultado_texto}
                    onHonorarioResultadoChange={(val) => setEditForm(prev => ({ ...prev, honorario_resultado_texto: val }))}
                    repartoMode={editForm.reparto_mode}
                    onRepartoModeChange={(val) => setEditForm(prev => ({ ...prev, reparto_mode: val }))}
                    primeraCuotaUf={editForm.primera_cuota_uf}
                    onPrimeraCuotaChange={(val) => setEditForm(prev => ({ ...prev, primera_cuota_uf: val }))}
                    fechaPrimeraCuota={editForm.fecha_primera_cuota}
                    onFechaPrimeraCuotaChange={(val) => setEditForm(prev => ({ ...prev, fecha_primera_cuota: val }))}
                    disabled={['Aceptada', 'Ejecución/Activa'].includes(selectedProposal?.status)}
                  />
                )}
                
                {/* Opciones avanzadas de cobranza - Editor */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div 
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${editForm.usar_pago_avanzado ? 'bg-amber-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                    onClick={() => setEditForm({ ...editForm, usar_pago_avanzado: !editForm.usar_pago_avanzado })}
                  >
                    <Checkbox
                      checked={editForm.usar_pago_avanzado}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, usar_pago_avanzado: checked })}
                      data-testid="edit-usar-pago-avanzado-checkbox"
                    />
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-slate-800">Opciones Avanzadas de Cobranza</span>
                    </div>
                  </div>
                  
                  {editForm.usar_pago_avanzado && (
                    <div className="p-4 bg-amber-50 border-t border-amber-200 space-y-4">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={editForm.descuento_pago_anticipado}
                          onCheckedChange={(checked) => setEditForm({ ...editForm, descuento_pago_anticipado: checked })}
                          data-testid="edit-descuento-anticipado-checkbox"
                        />
                        <div>
                          <Label className="text-sm text-amber-800 font-medium">Descuento 15% por Pago Anticipado</Label>
                          <p className="text-xs text-amber-600">Ofrecer descuento si el cliente paga todas las cuotas por adelantado.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                        <Checkbox
                          checked={editForm.billing_start_mode === 'fixed_date'}
                          onCheckedChange={(checked) => {
                            if (checked) setEditForm({ ...editForm, billing_start_mode: 'fixed_date', related_psp_id: '' });
                            else setEditForm({ ...editForm, billing_start_mode: 'immediate', billing_start_date: '' });
                          }}
                          data-testid="edit-inicio-diferido-checkbox"
                        />
                        <div className="flex-1">
                          <Label className="text-sm text-amber-800 font-medium">Diferir Inicio de Cobranza</Label>
                          <p className="text-xs text-amber-600">La primera cuota comenzar\u00e1 en una fecha futura espec\u00edfica.</p>
                        </div>
                      </div>
                      {editForm.billing_start_mode === 'fixed_date' && (
                        <div className="ml-7 p-3 bg-amber-50 rounded-lg">
                          <Label className="text-sm text-amber-800">Fecha de inicio</Label>
                          <Input type="date" value={editForm.billing_start_date} onChange={(e) => setEditForm({ ...editForm, billing_start_date: e.target.value })} min={new Date().toISOString().split('T')[0]} className="mt-1" data-testid="edit-billing-start-date-input" />
                        </div>
                      )}
                      <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                        <Checkbox
                          checked={editForm.billing_start_mode === 'after_related_psp'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setEditForm({ ...editForm, billing_start_mode: 'after_related_psp', billing_start_date: '' });
                              if (selectedProposal?.client_id) fetchAvailablePspsForSubordination(selectedProposal.client_id);
                            } else setEditForm({ ...editForm, billing_start_mode: 'immediate', related_psp_id: '' });
                          }}
                          data-testid="edit-cobranza-subordinada-checkbox"
                        />
                        <div className="flex-1">
                          <Label className="text-sm text-amber-800 font-medium">Subordinar a otra PSP</Label>
                          <p className="text-xs text-amber-600">La cobranza iniciar\u00e1 al finalizar otra PSP del mismo cliente.</p>
                        </div>
                      </div>
                      {editForm.billing_start_mode === 'after_related_psp' && (
                        <div className="ml-7 p-3 bg-amber-50 rounded-lg">
                          {availablePspsForSubordination.length === 0 ? (
                            <p className="text-xs text-amber-700">No hay PSPs activas para este cliente</p>
                          ) : (
                            <div>
                              <Label className="text-sm text-amber-800">PSP que debe finalizar primero</Label>
                              <Select value={editForm.related_psp_id} onValueChange={(value) => setEditForm({ ...editForm, related_psp_id: value })}>
                                <SelectTrigger className="mt-1" data-testid="edit-related-psp-select"><SelectValue placeholder="Seleccionar PSP" /></SelectTrigger>
                                <SelectContent>
                                  {availablePspsForSubordination.map((psp) => (
                                    <SelectItem key={psp.id} value={psp.id}>{psp.folio} - {psp.titulo?.substring(0, 30)}...</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSaveChanges} disabled={saving} className="flex-1" data-testid="save-changes-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                  <Button variant="outline" onClick={() => handleGeneratePDF(selectedProposal.id, true)} data-testid="preview-pdf-btn">
                    <Eye className="w-4 h-4 mr-2" />
                    Previsualizar PDF
                  </Button>
                  <Button variant="outline" onClick={() => handleGeneratePDF(selectedProposal.id, false)} data-testid="download-pdf-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </Button>
                </div>
              </TabsContent>
              
              {/* Minutas Tab */}
              <TabsContent value="minutas" className="space-y-4 mt-4">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold text-slate-900">Minutas Asociadas a esta PSP</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Acceda rápidamente a los PDFs de las reuniones para tener contexto durante la negociación.
                  </p>
                </div>
                
                {proposalMinutas.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay minutas asociadas a esta PSP</p>
                    <p className="text-xs mt-1">Puede asociar minutas al crear una nueva PSP</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {proposalMinutas.map((minuta) => (
                      <div 
                        key={minuta.id}
                        className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="w-4 h-4 text-primary" />
                            <h4 className="font-medium text-slate-900">{minuta.title}</h4>
                            {minuta.transferido_operaciones && (
                              <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                                Transferida
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">
                            {minuta.client_name} • {minuta.tareas_count} tareas identificadas
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(minuta.created_at).toLocaleDateString('es-CL')}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewMinutaPDF(minuta.id)}
                          data-testid={`view-minuta-pdf-${minuta.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              {/* Estado Tab */}
              <TabsContent value="estado" className="space-y-4 mt-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-base mb-3 block">Estado Actual</Label>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-medium ${getStatusColor(selectedProposal.status)}`}>
                    {getStatusIcon(selectedProposal.status)}
                    {selectedProposal.status}
                  </div>
                </div>
                
                <div>
                  <Label className="text-base mb-3 block">Cambiar Estado</Label>
                  {(['Aceptada', 'Ejecución/Activa'].includes(selectedProposal.status)) && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                      Esta PSP ya fue activada. Los estados anteriores (Borrador, En Negociación, Enviada, Primer Pago) están bloqueados para preservar la trazabilidad de tareas, alertas y facturación.
                    </p>
                  )}
                  {selectedProposal.status === 'Primer Pago' && (
                    <p className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded p-2 mb-3">
                      Esta PSP está pre-aprobada. Para activarla, usa la pestaña <strong>Primer Pago</strong> y haz clic en "Confirmar Pago Recibido" — solo así se generará el CPS y se liberarán las tareas al Tablero de Líderes.
                    </p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {STATUS_OPTIONS.map((status) => {
                      const isCurrentStatus = selectedProposal.status === status.value;
                      const isPostActivation = ['Aceptada', 'Ejecución/Activa'].includes(selectedProposal.status);
                      const isPreActivationTarget = ['Borrador', 'En Negociación', 'Enviada', 'Primer Pago'].includes(status.value);
                      const blockedDegrade = isPostActivation && isPreActivationTarget;
                      // Aceptada/Ejecución solo accesible vía Confirmar Pago Recibido desde Primer Pago
                      const requiresConfirmPayment = ['Aceptada', 'Ejecución/Activa'].includes(status.value) && selectedProposal.status !== 'Primer Pago';
                      const disabled = isCurrentStatus || blockedDegrade || requiresConfirmPayment;
                      return (
                        <Button
                          key={status.value}
                          variant={isCurrentStatus ? 'default' : 'outline'}
                          className={isCurrentStatus ? 'bg-primary' : (status.value === 'Primer Pago' ? 'border-orange-300 text-orange-700 hover:bg-orange-50' : '')}
                          onClick={() => handleStatusChange(selectedProposal.id, status.value)}
                          disabled={disabled}
                          title={blockedDegrade ? 'Bloqueado: la PSP ya fue activada' : (requiresConfirmPayment ? 'Para aceptar, pasa primero por Primer Pago y confirma el pago recibido' : '')}
                          data-testid={`status-btn-${status.value.replace(/\s/g, '-').replace(/\//g, '-').toLowerCase()}`}
                        >
                          {status.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                {selectedProposal.status !== 'Aceptada' && selectedProposal.status !== 'Ejecución/Activa' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-green-800 mb-2">Aceptar PSP</h4>
                    <p className="text-sm text-green-700 mb-3">
                      Al aceptar esta PSP se ejecutarán los siguientes triggers automáticos:
                    </p>
                    <ul className="text-sm text-green-700 list-disc list-inside space-y-1">
                      <li>Crear tareas en el Tablero de Líderes (pendientes de asignación)</li>
                      <li>Alertas a líderes de área (Legal, Tributaria, Contabilidad)</li>
                      <li>Alerta a Finanzas para facturar primera cuota</li>
                      <li>Registro del plan de cuotas como ingresos proyectados</li>
                      <li>Marcar minuta como "Transferida a Operaciones"</li>
                    </ul>
                    <Button
                      className="mt-4 bg-green-600 hover:bg-green-700"
                      onClick={() => handleStatusChange(selectedProposal.id, 'Aceptada')}
                      data-testid="accept-psp-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aceptar PSP y Ejecutar Triggers
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              {/* Bitácora Tab */}
              <TabsContent value="bitacora" className="space-y-4 mt-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-3">Agregar Nota de Seguimiento</h4>
                  <div className="space-y-3">
                    <div>
                      <Label>Tipo de Gestión</Label>
                      <Select
                        value={newBitacoraEntry.tipo}
                        onValueChange={(value) => setNewBitacoraEntry({ ...newBitacoraEntry, tipo: value })}
                      >
                        <SelectTrigger data-testid="bitacora-tipo-select">
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Llamada">Llamada telefónica</SelectItem>
                          <SelectItem value="Correo">Correo electrónico</SelectItem>
                          <SelectItem value="Reunión">Reunión</SelectItem>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                          <SelectItem value="Visita">Visita presencial</SelectItem>
                          <SelectItem value="Nota">Nota interna</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <Textarea
                        value={newBitacoraEntry.descripcion}
                        onChange={(e) => setNewBitacoraEntry({ ...newBitacoraEntry, descripcion: e.target.value })}
                        rows={3}
                        placeholder="Describe la gestión realizada..."
                        data-testid="bitacora-descripcion-input"
                      />
                    </div>
                    <Button onClick={handleAddBitacoraEntry} className="w-full" data-testid="add-bitacora-btn">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Agregar a Bitácora
                    </Button>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Historial de Gestiones</h4>
                  {selectedProposal.bitacora_comercial?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedProposal.bitacora_comercial.slice().reverse().map((entry, idx) => (
                        <div key={idx} className="bg-white border border-slate-200 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-primary">{entry.tipo}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(entry.fecha).toLocaleString('es-CL')}
                            </span>
                          </div>
                          <p className="text-slate-700">{entry.descripcion}</p>
                          <p className="text-xs text-slate-500 mt-2">Registrado por: {entry.usuario}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay entradas en la bitácora aún</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Datos de Facturación + Contrato */}
      <Dialog open={showFacturacionModal} onOpenChange={setShowFacturacionModal}>
        <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-green-600" />
              Datos para Aceptar PSP
            </DialogTitle>
            <DialogDescription>
              Complete los datos de facturación y del contrato para aceptar la PSP {proposalToAccept?.folio}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* ===== SECCIÓN 1: DATOS DE FACTURACIÓN ===== */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <h3 className="text-base font-semibold text-slate-900">Datos para Facturación</h3>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Importante:</strong> Estos datos serán utilizados por Finanzas para la emisión de facturas.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="fact-nombre" className="text-sm font-medium">
                    Nombre / Razón Social <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fact-nombre"
                    value={datosFacturacion.nombre_razon_social}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, nombre_razon_social: e.target.value})}
                    placeholder="Nombre o Razón Social para facturación"
                    data-testid="facturacion-nombre-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fact-rut" className="text-sm font-medium">
                    RUT <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fact-rut"
                    value={datosFacturacion.rut}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, rut: e.target.value})}
                    placeholder="Ej: 76.XXX.XXX-X"
                    data-testid="facturacion-rut-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fact-contacto-nombre" className="text-sm font-medium">
                    Contacto de Facturación <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fact-contacto-nombre"
                    value={datosFacturacion.contacto_nombre}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, contacto_nombre: e.target.value})}
                    placeholder="Nombre del contacto"
                    data-testid="facturacion-contacto-nombre-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fact-email" className="text-sm font-medium">
                    Email de Contacto <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fact-email"
                    type="email"
                    value={datosFacturacion.contacto_email}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, contacto_email: e.target.value})}
                    placeholder="correo@empresa.com"
                    data-testid="facturacion-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fact-telefono" className="text-sm font-medium">
                    Teléfono de Contacto <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fact-telefono"
                    value={datosFacturacion.contacto_telefono}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, contacto_telefono: e.target.value})}
                    placeholder="+56 9 XXXX XXXX"
                    data-testid="facturacion-telefono-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="fact-notas" className="text-sm font-medium">
                    Notas Adicionales
                  </Label>
                  <Textarea
                    id="fact-notas"
                    value={datosFacturacion.notas}
                    onChange={(e) => setDatosFacturacion({...datosFacturacion, notas: e.target.value})}
                    placeholder="Instrucciones especiales de facturación, dirección, etc."
                    rows={2}
                    data-testid="facturacion-notas-input"
                  />
                </div>
              </div>
            </div>
            
            {/* ===== CHECKBOX: USAR MISMOS DATOS ===== */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <Checkbox
                id="usar-mismos-datos"
                checked={usarMismosDatos}
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  setUsarMismosDatos(isChecked);
                  if (isChecked) {
                    // Auto-completar contrato desde facturación
                    setDatosContrato({
                      representante_legal: datosFacturacion.contacto_nombre,
                      rut_representante: datosFacturacion.rut,
                      domicilio_legal: datosFacturacion.notas || datosContrato.domicilio_legal || ''
                    });
                  }
                }}
                data-testid="checkbox-usar-mismos-datos"
              />
              <div className="flex-1">
                <label htmlFor="usar-mismos-datos" className="text-sm font-medium text-slate-900 cursor-pointer">
                  Usar los mismos datos para el contrato
                </label>
                <p className="text-xs text-slate-600 mt-0.5">
                  Auto-completa los datos del contrato con los de facturación. Podrás editarlos después.
                </p>
              </div>
            </div>
            
            {/* ===== SECCIÓN 2: DATOS PARA EL CONTRATO ===== */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                <FileSignature className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-semibold text-slate-900">Datos para el Contrato (CPS)</h3>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Importante:</strong> Estos datos aparecerán en el contrato (compareciente y firmas).
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="contrato-representante" className="text-sm font-medium">
                    Representante Legal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contrato-representante"
                    value={datosContrato.representante_legal}
                    onChange={(e) => setDatosContrato({...datosContrato, representante_legal: e.target.value})}
                    placeholder="Nombre completo del representante legal"
                    data-testid="contrato-representante-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contrato-rut-rep" className="text-sm font-medium">
                    RUT Representante <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contrato-rut-rep"
                    value={datosContrato.rut_representante}
                    onChange={(e) => setDatosContrato({...datosContrato, rut_representante: e.target.value})}
                    placeholder="Ej: 12.345.678-9"
                    data-testid="contrato-rut-representante-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contrato-domicilio" className="text-sm font-medium">
                    Domicilio Legal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contrato-domicilio"
                    value={datosContrato.domicilio_legal}
                    onChange={(e) => setDatosContrato({...datosContrato, domicilio_legal: e.target.value})}
                    placeholder="Ej: Av. Apoquindo 4500, Las Condes"
                    data-testid="contrato-domicilio-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowFacturacionModal(false);
                  setProposalToAccept(null);
                }}
                data-testid="facturacion-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleAcceptWithFacturacion}
                data-testid="facturacion-confirm-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmar y Aceptar PSP
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Ver Datos de Facturación */}
      <Dialog open={showViewFacturacionModal} onOpenChange={setShowViewFacturacionModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Datos de Facturación
            </DialogTitle>
            <DialogDescription>
              Información de facturación de {viewingFacturacion?.folio}
            </DialogDescription>
          </DialogHeader>
          
          {viewingFacturacion?.datos_facturacion && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Razón Social / Nombre</p>
                  <p className="text-slate-900 font-semibold">{viewingFacturacion.datos_facturacion.nombre_razon_social}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">RUT</p>
                  <p className="text-slate-900 font-mono">{viewingFacturacion.datos_facturacion.rut}</p>
                </div>
                
                <hr className="border-slate-200" />
                
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Contacto de Facturación</p>
                  <p className="text-slate-900">{viewingFacturacion.datos_facturacion.contacto_nombre}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Email</p>
                    <p className="text-slate-900 text-sm">{viewingFacturacion.datos_facturacion.contacto_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Teléfono</p>
                    <p className="text-slate-900">{viewingFacturacion.datos_facturacion.contacto_telefono}</p>
                  </div>
                </div>
                
                {viewingFacturacion.datos_facturacion.notas && (
                  <>
                    <hr className="border-slate-200" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Notas Adicionales</p>
                      <p className="text-slate-700 text-sm italic">{viewingFacturacion.datos_facturacion.notas}</p>
                    </div>
                  </>
                )}
                
                {viewingFacturacion.datos_facturacion.fecha_registro && (
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-xs text-slate-400">
                      Registrado el {new Date(viewingFacturacion.datos_facturacion.fecha_registro).toLocaleDateString('es-CL', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowViewFacturacionModal(false);
                    setViewingFacturacion(null);
                  }}
                  data-testid="view-facturacion-close-btn"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación para Eliminar PSP */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Eliminar Propuesta
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-red-800">
                ¿Está seguro que desea eliminar la PSP <strong>{proposalToDelete?.folio}</strong>?
                <br />
                <span className="text-sm">
                  Se eliminarán también las tareas, facturas y alertas asociadas.
                  {['Aceptada', 'Ejecución/Activa'].includes(proposalToDelete?.status) && (
                    <><br /><strong className="text-red-700">⚠️ Esta es una PSP Aceptada/CPS. Esta acción es irreversible.</strong></>
                  )}
                </span>
              </AlertDescription>
            </Alert>
            
            {proposalToDelete && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Título:</strong> {proposalToDelete.titulo}</p>
                <p><strong>Estado:</strong> {proposalToDelete.status}</p>
                <p><strong>Valor:</strong> {proposalToDelete.precio_uf ? `${proposalToDelete.precio_uf.toFixed(2)} UF` : 'N/A'}</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setProposalToDelete(null);
              }}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProposal}
              disabled={deleting}
              data-testid="confirm-delete-psp-btn"
            >
              {deleting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar PSP
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Agregar/Editar Ítem de Cobro */}
      <Dialog open={showAddItemModal} onOpenChange={(open) => {
        if (!open) {
          setShowAddItemModal(false);
          setEditingItem(null);
          setNewItem({ tipo: 'tarea', nombre: '', descripcion: '', valor_uf: '', tarea_ids: [] });
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTree className="w-5 h-5 text-emerald-600" />
              {editingItem ? 'Editar Ítem de Cobro' : 'Agregar Ítem de Cobro'}
            </DialogTitle>
            <DialogDescription>
              Seleccione las tareas a incluir y asigne un valor en UF
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Selector de Tareas */}
            <div>
              <Label className="text-sm font-medium">
                Seleccionar Tareas <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                {!newItem.tarea_ids || newItem.tarea_ids.length === 0 
                  ? 'Seleccione una o más tareas de las minutas asociadas'
                  : `${newItem.tarea_ids.length} tarea(s) seleccionada(s)`}
              </p>
              
              {loadingTareas ? (
                <div className="text-center py-4 text-slate-500">Cargando tareas...</div>
              ) : !tareasDisponibles || tareasDisponibles.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                  <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-sm text-slate-600">No hay tareas disponibles</p>
                  <p className="text-xs text-slate-500">Las minutas asociadas no tienen tareas o ya fueron asignadas</p>
                </div>
              ) : (
                <div className="max-h-[200px] overflow-y-auto border rounded-lg divide-y">
                  {tareasDisponibles.map((tarea) => {
                    const isSelected = newItem.tarea_ids?.includes(tarea.id) || false;
                    const isDisabled = tarea.asignada && !isSelected;
                    
                    return (
                      <div 
                        key={tarea.id}
                        className={`p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                          isDisabled 
                            ? 'bg-slate-100 opacity-50 cursor-not-allowed' 
                            : isSelected 
                              ? 'bg-emerald-50' 
                              : 'hover:bg-slate-50'
                        }`}
                        onClick={() => {
                          if (isDisabled) return;
                          setNewItem(prev => ({
                            ...prev,
                            tarea_ids: isSelected 
                              ? (prev.tarea_ids || []).filter(id => id !== tarea.id)
                              : [...(prev.tarea_ids || []), tarea.id]
                          }));
                        }}
                      >
                        <Checkbox 
                          checked={isSelected}
                          disabled={isDisabled}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-slate-900 truncate">
                              {tarea.titulo}
                            </span>
                            <Badge variant="outline" className="text-xs shrink-0">
                              {tarea.area}
                            </Badge>
                            {isDisabled && (
                              <Badge variant="secondary" className="text-xs shrink-0 bg-amber-100 text-amber-700">
                                Ya asignada
                              </Badge>
                            )}
                          </div>
                          {tarea.descripcion && (
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                              {tarea.descripcion}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Nombre personalizado (opcional) */}
            <div>
              <Label className="text-sm font-medium">Nombre del Ítem (opcional)</Label>
              <Input
                value={newItem.nombre}
                onChange={(e) => setNewItem({ ...newItem, nombre: e.target.value })}
                placeholder={
                  !newItem.tarea_ids || newItem.tarea_ids.length === 0 
                    ? 'Se generará automáticamente' 
                    : newItem.tarea_ids.length === 1 
                      ? (tareasDisponibles?.find(t => t.id === newItem.tarea_ids[0])?.titulo || 'Nombre de la tarea')
                      : `Grupo de ${newItem.tarea_ids.length} tareas`
                }
                className="mt-1"
                data-testid="item-nombre-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Deje vacío para usar el nombre automático basado en las tareas
              </p>
            </div>
            
            {/* Descripción adicional */}
            <div>
              <Label className="text-sm font-medium">Notas adicionales (opcional)</Label>
              <Textarea
                value={newItem.descripcion}
                onChange={(e) => setNewItem({ ...newItem, descripcion: e.target.value })}
                placeholder="Detalles adicionales sobre este ítem de cobro..."
                rows={2}
                className="mt-1"
                data-testid="item-descripcion-input"
              />
            </div>
            
            {/* Valor en UF */}
            <div>
              <Label className="text-sm font-medium">
                Valor (UF) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={newItem.valor_uf}
                onChange={(e) => setNewItem({ ...newItem, valor_uf: e.target.value })}
                placeholder="0.00"
                className="mt-1"
                data-testid="item-valor-input"
              />
              {newItem.valor_uf && (
                <p className="text-xs text-slate-500 mt-1">
                  ≈ {formatCLP(ufToCLP(parseFloat(newItem.valor_uf)))} CLP
                </p>
              )}
            </div>
            
            {/* Resumen de selección */}
            {newItem.tarea_ids && newItem.tarea_ids.length > 0 && tareasDisponibles && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm font-medium text-emerald-800">
                  Resumen: {newItem.tarea_ids.length === 1 ? 'Tarea Individual' : `Grupo de ${newItem.tarea_ids.length} Tareas`}
                </p>
                <ul className="mt-1 text-xs text-emerald-700 list-disc list-inside">
                  {newItem.tarea_ids.map(tid => {
                    const tarea = tareasDisponibles.find(t => t.id === tid);
                    return tarea ? (
                      <li key={tid} className="truncate">{tarea.titulo}</li>
                    ) : null;
                  })}
                </ul>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddItemModal(false);
                  setEditingItem(null);
                  setNewItem({ tipo: 'tarea', nombre: '', descripcion: '', valor_uf: '', tarea_ids: [] });
                }}
              >
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={editingItem ? handleUpdateItemCobro : handleAddItemCobro}
                disabled={!newItem.tarea_ids || newItem.tarea_ids.length === 0 || !newItem.valor_uf}
                data-testid="save-item-cobro-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {editingItem ? 'Actualizar' : 'Agregar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de confirmación para eliminar ítem de cobro */}
      <Dialog open={showDeleteItemModal} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteItemModal(false);
          setItemToDelete(null);
        }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Ítem de Cobro
            </DialogTitle>
            <DialogDescription>
              ¿Está seguro que desea eliminar este ítem de cobro?
            </DialogDescription>
          </DialogHeader>
          
          {itemToDelete && (
            <div className="py-4">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={itemToDelete.tipo === 'grupo' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                    {itemToDelete.tipo === 'grupo' ? 'Grupo' : 'Tarea'}
                  </Badge>
                  <span className="font-medium">{itemToDelete.nombre}</span>
                </div>
                <p className="text-sm text-slate-600">Valor: {itemToDelete.valor_uf?.toFixed(2)} UF</p>
              </div>
              <p className="text-sm text-amber-600 mt-3">
                Las tareas asociadas volverán a estar disponibles para asignar.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteItemModal(false);
                setItemToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => itemToDelete && handleDeleteItemCobro(itemToDelete.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* ===== MODAL DE FRAGMENTACIÓN DE PSP ===== */}
      <Dialog open={showFragmentacionModal} onOpenChange={setShowFragmentacionModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              Fragmentar PSP: {fragmentacionPSP?.folio}
            </DialogTitle>
          </DialogHeader>
          
          {loadingFragmentacion ? (
            <div className="py-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-slate-500">Cargando tareas...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info del cliente */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{fragmentacionPSP?.client_name}</p>
                <p className="text-sm text-slate-500">Estado actual: {fragmentacionPSP?.status}</p>
              </div>
              
              {/* PSPs relacionadas */}
              {(pspsRelacionadas.psp_madre || pspsRelacionadas.psps_hijas?.length > 0) && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    PSPs Relacionadas
                  </h4>
                  {pspsRelacionadas.psp_madre && (
                    <p className="text-sm text-purple-700">
                      Esta es una fragmentación de: <strong>{pspsRelacionadas.psp_madre.folio}</strong>
                    </p>
                  )}
                  {pspsRelacionadas.psps_hijas?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-purple-700 mb-1">Fragmentaciones creadas:</p>
                      <div className="flex flex-wrap gap-2">
                        {pspsRelacionadas.psps_hijas.map(hija => (
                          <Badge key={hija.id} variant="outline" className="bg-white">
                            {hija.folio} - {hija.categoria_servicio}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Selección de tareas */}
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Seleccionar destino de cada tarea</h4>
                <p className="text-sm text-slate-500 mb-3">
                  Para cada tarea, elija si desea incluirla en una nueva PSP o guardarla como oportunidad futura.
                </p>
                
                {tareasFragmentables.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                    <p className="text-slate-600">No hay tareas disponibles para fragmentar</p>
                    <p className="text-sm text-slate-400">Esta PSP no tiene minutas con tareas asociadas</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto border rounded-lg p-3">
                    {tareasFragmentables.map(tarea => {
                      const enPSP = tareasSeleccionadasFrag.includes(tarea.id);
                      const diferir = tareasDiferirFrag.includes(tarea.id);
                      
                      return (
                        <div 
                          key={tarea.id}
                          className={`p-3 rounded-lg border transition-colors ${
                            tarea.ya_extraida 
                              ? 'bg-slate-100 border-slate-200 opacity-60' 
                              : enPSP
                                ? 'bg-purple-50 border-purple-300'
                                : diferir
                                  ? 'bg-amber-50 border-amber-300'
                                  : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={
                                  tarea.area === 'Legal' ? 'bg-blue-100 text-blue-700' :
                                  tarea.area === 'Tributaria' ? 'bg-amber-100 text-amber-700' :
                                  tarea.area === 'Contabilidad' ? 'bg-green-100 text-green-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {tarea.area || 'Sin área'}
                                </Badge>
                                <span className="font-medium text-slate-800 text-sm">{tarea.title || tarea.titulo}</span>
                              </div>
                              {(tarea.description || tarea.descripcion) && (
                                <p className="text-xs text-slate-500 line-clamp-2">{tarea.description || tarea.descripcion}</p>
                              )}
                              {tarea.ya_extraida && (
                                <p className="text-xs text-purple-600 mt-1">Ya extraída a otra PSP</p>
                              )}
                            </div>
                            
                            {/* Botones de selección */}
                            {!tarea.ya_extraida && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleToggleTareaPSP(tarea.id)}
                                  className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                                    enPSP 
                                      ? 'bg-purple-600 text-white' 
                                      : 'bg-slate-100 text-slate-600 hover:bg-purple-100'
                                  }`}
                                  title="Añadir a nueva PSP fragmentada"
                                >
                                  <Plus className="w-3 h-3" />
                                  Añadir a nueva PSP
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleTareaDiferir(tarea.id)}
                                  className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                                    diferir 
                                      ? 'bg-amber-500 text-white' 
                                      : 'bg-slate-100 text-slate-600 hover:bg-amber-100'
                                  }`}
                                  title="Guardar como oportunidad futura"
                                >
                                  <Lightbulb className="w-3 h-3" />
                                  Diferir
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Resumen de selección */}
                <div className="flex gap-4 mt-3 text-sm">
                  {tareasSeleccionadasFrag.length > 0 && (
                    <span className="text-purple-600">
                      {tareasSeleccionadasFrag.length} para nueva PSP
                    </span>
                  )}
                  {tareasDiferirFrag.length > 0 && (
                    <span className="text-amber-600">
                      {tareasDiferirFrag.length} para oportunidad futura
                    </span>
                  )}
                </div>
              </div>
              
              {/* Configuración de nueva PSP */}
              {tareasSeleccionadasFrag.length > 0 && (
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-800">Configuración de la nueva PSP</h4>
                  
                  <div>
                    <Label className="text-slate-700">Categoría de Servicio *</Label>
                    <select
                      value={fragmentacionForm.categoria_servicio}
                      onChange={(e) => setFragmentacionForm(prev => ({ ...prev, categoria_servicio: e.target.value }))}
                      className="w-full mt-1 p-2 border border-slate-200 rounded-md"
                    >
                      <option value="">Seleccionar categoría...</option>
                      <option value="Servicios Legales">Servicios Legales</option>
                      <option value="Servicios Tributarios">Servicios Tributarios</option>
                      <option value="Servicios Contables">Servicios Contables</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700">Tipo de Servicio</Label>
                    <select
                      value={fragmentacionForm.tipo_servicio}
                      onChange={(e) => setFragmentacionForm(prev => ({ ...prev, tipo_servicio: e.target.value }))}
                      className="w-full mt-1 p-2 border border-slate-200 rounded-md"
                    >
                      {TIPOS_SERVICIO.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700">Descripción del Alcance</Label>
                    <textarea
                      value={fragmentacionForm.descripcion_alcance}
                      onChange={(e) => setFragmentacionForm(prev => ({ ...prev, descripcion_alcance: e.target.value }))}
                      placeholder="Ej: Reconstrucción Contabilidad 2024-2025, Defensa ante Anotación 5522..."
                      className="w-full mt-1 p-2 border border-slate-200 rounded-md resize-none"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-700">Precio (UF)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={fragmentacionForm.precio_uf}
                      onChange={(e) => setFragmentacionForm(prev => ({ ...prev, precio_uf: e.target.value }))}
                      placeholder="Precio para esta sub-propuesta"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
              
              {/* Sección de tareas diferidas */}
              {tareasDiferirFrag.length > 0 && tareasSeleccionadasFrag.length === 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Tareas a diferir como oportunidad futura
                  </h4>
                  <p className="text-sm text-amber-700">
                    {tareasDiferirFrag.length} tarea(s) se guardarán como oportunidades comerciales para retomar después.
                  </p>
                </div>
              )}
              
              {/* Botones de acción */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowFragmentacionModal(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleCrearFragmentacion}
                  disabled={(tareasSeleccionadasFrag.length === 0 && tareasDiferirFrag.length === 0) || 
                           (tareasSeleccionadasFrag.length > 0 && !fragmentacionForm.categoria_servicio) || 
                           saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Procesando...
                    </>
                  ) : tareasSeleccionadasFrag.length > 0 && tareasDiferirFrag.length > 0 ? (
                    <>
                      <Layers className="w-4 h-4 mr-2" />
                      Crear PSP y Diferir
                    </>
                  ) : tareasSeleccionadasFrag.length > 0 ? (
                    <>
                      <GitBranch className="w-4 h-4 mr-2" />
                      Crear PSP Fragmentada
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Guardar como Oportunidades
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal para convertir oportunidades a PSP */}
      <Dialog open={showConvertirModal} onOpenChange={setShowConvertirModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Crear PSP desde Oportunidades
            </DialogTitle>
            <DialogDescription>
              Convierte {selectedOportunidades.length} oportunidad(es) en una nueva Propuesta de Servicios Profesionales
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Título */}
            <div>
              <Label htmlFor="convertir-titulo">Título de la PSP</Label>
              <Input
                id="convertir-titulo"
                value={convertirForm.titulo}
                onChange={(e) => setConvertirForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ej: Servicios de Asesoría Tributaria"
                data-testid="convertir-titulo-input"
              />
            </div>
            
            {/* Tipo de servicio */}
            <div>
              <Label htmlFor="convertir-tipo">Tipo de Servicio</Label>
              <Select
                value={convertirForm.tipo_servicio}
                onValueChange={(value) => setConvertirForm(prev => ({ ...prev, tipo_servicio: value }))}
              >
                <SelectTrigger data-testid="convertir-tipo-select">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_SERVICIO.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Descripción */}
            <div>
              <Label htmlFor="convertir-descripcion">Descripción</Label>
              <Textarea
                id="convertir-descripcion"
                value={convertirForm.descripcion}
                onChange={(e) => setConvertirForm(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción del alcance de los servicios..."
                rows={3}
                data-testid="convertir-descripcion-input"
              />
            </div>
            
            {/* Precio */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="convertir-precio">Precio (UF)</Label>
                <Input
                  id="convertir-precio"
                  type="number"
                  value={convertirForm.precio_uf}
                  onChange={(e) => setConvertirForm(prev => ({ ...prev, precio_uf: e.target.value }))}
                  placeholder="Ej: 50"
                  data-testid="convertir-precio-input"
                />
              </div>
              <div>
                <Label htmlFor="convertir-meses">Meses Ejecución</Label>
                <Input
                  id="convertir-meses"
                  type="number"
                  value={convertirForm.meses_ejecucion}
                  onChange={(e) => setConvertirForm(prev => ({ ...prev, meses_ejecucion: e.target.value }))}
                  placeholder="3"
                  data-testid="convertir-meses-input"
                />
              </div>
              <div>
                <Label htmlFor="convertir-cuotas">Número Cuotas</Label>
                <Input
                  id="convertir-cuotas"
                  type="number"
                  value={convertirForm.num_cuotas}
                  onChange={(e) => setConvertirForm(prev => ({ ...prev, num_cuotas: e.target.value }))}
                  placeholder="1"
                  data-testid="convertir-cuotas-input"
                />
              </div>
            </div>
            
            {/* Inicio Diferido de Cobranza */}
            <div className="border border-amber-200 rounded-lg p-4 space-y-3 bg-amber-50/50">
              <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Opciones de Cobranza
              </h4>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={convertirForm.billing_start_mode === 'fixed_date'}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setConvertirForm(prev => ({ ...prev, billing_start_mode: 'fixed_date', related_psp_id: '' }));
                    } else {
                      setConvertirForm(prev => ({ ...prev, billing_start_mode: 'immediate', billing_start_date: '' }));
                    }
                  }}
                  data-testid="convertir-inicio-diferido-checkbox"
                />
                <div className="flex-1">
                  <Label className="text-sm text-amber-800">Diferir Inicio de Cobranza</Label>
                  <p className="text-xs text-amber-600">La primera cuota comenzará en una fecha futura.</p>
                </div>
              </div>
              
              {convertirForm.billing_start_mode === 'fixed_date' && (
                <div className="ml-7">
                  <Label className="text-sm text-amber-800">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={convertirForm.billing_start_date}
                    onChange={(e) => setConvertirForm(prev => ({ ...prev, billing_start_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                    data-testid="convertir-billing-start-date-input"
                  />
                </div>
              )}
              
              <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                <Checkbox
                  checked={convertirForm.billing_start_mode === 'after_related_psp'}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setConvertirForm(prev => ({ ...prev, billing_start_mode: 'after_related_psp', billing_start_date: '' }));
                      // Cargar PSPs del primer cliente de las oportunidades seleccionadas
                      const firstOpp = oportunidadesDiferidas.find(o => selectedOportunidades.includes(o.id));
                      if (firstOpp?.client_id) {
                        fetchAvailablePspsForSubordination(firstOpp.client_id);
                      }
                    } else {
                      setConvertirForm(prev => ({ ...prev, billing_start_mode: 'immediate', related_psp_id: '' }));
                    }
                  }}
                  data-testid="convertir-subordinada-checkbox"
                />
                <div className="flex-1">
                  <Label className="text-sm text-amber-800">Subordinar a otra PSP</Label>
                  <p className="text-xs text-amber-600">La cobranza iniciará al finalizar otra PSP.</p>
                </div>
              </div>
              
              {convertirForm.billing_start_mode === 'after_related_psp' && (
                <div className="ml-7">
                  {availablePspsForSubordination.length === 0 ? (
                    <p className="text-xs text-amber-700">No hay PSPs activas disponibles</p>
                  ) : (
                    <div>
                      <Label className="text-sm text-amber-800">PSP que debe finalizar primero</Label>
                      <Select 
                        value={convertirForm.related_psp_id} 
                        onValueChange={(value) => setConvertirForm(prev => ({ ...prev, related_psp_id: value }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="convertir-related-psp-select">
                          <SelectValue placeholder="Seleccionar PSP" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePspsForSubordination.map((psp) => (
                            <SelectItem key={psp.id} value={psp.id}>
                              {psp.folio} - {psp.titulo?.substring(0, 25)}...
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConvertirModal(false);
                  setSelectedOportunidades([]);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConvertirOportunidades}
                disabled={saving || !convertirForm.titulo || !convertirForm.precio_uf}
                data-testid="btn-confirmar-convertir"
              >
                {saving ? 'Creando...' : 'Crear PSP'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Anexos Contractuales */}
      <AnexosContractuales
        psp={selectedPSPAnexo}
        isOpen={showAnexosModal}
        onClose={() => {
          setShowAnexosModal(false);
          setSelectedPSPAnexo(null);
        }}
        onAnexoAplicado={() => {
          fetchData();
          toast.success('Contrato actualizado con el anexo aplicado');
        }}
      />
    </div>
  );
};

export default Propuestas;
